import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MakeSeparateQuoteButton } from '@/components/MakeSeparateQuoteButton';
import { Loader2, Save, RefreshCw, AlertCircle, AlertTriangle, Layers, Wrench, Pipette, Triangle, GripHorizontal, Undo2, Sparkles, Check, MapPin, X, Star, Copy, Trash2, Printer, Brush, PaintBucket, Pencil, Calculator, ChevronUp, ChevronDown, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EditRateLink from '@/components/EditRateLink';
import RateDetailEditButton, { RateDetailProvider, useRateDetail } from '@/components/RateDetailEditButton';
import AquasealRepairCardRow from '@/components/shared/ComponentRow/AquasealRepairCardRow';
import ComponentsFlashingEmbed from '@/components/shared/ComponentRow/ComponentsFlashingEmbed';
import { toast } from 'sonner';

/** Visual config for the Stage-1 Repair Option picker. */
const REPAIR_OPTIONS = [
  { key: 'full_bp',       title: 'Full Bed & Point',         tone: 'rose',   desc: 'Total ridging × Bed & Point rate. Use when foundations are heavily damaged / loose.' },
  { key: 'full_repoint',  title: 'Full Repoint',             tone: 'sky',    desc: 'Total ridging × Point rate. Use when foundations are still strong (light cracking).' },
  { key: 'part',          title: 'Part B&P + Part Repoint',  tone: 'amber',  desc: 'Mixed quality — per-ridge split. Worst sections get B&P, the rest get Repoint.' },
  { key: 'replace_all',   title: 'Replace ALL Ridging',      tone: 'teal', desc: 'Total length ÷ cap length × cap rate. Use when ridges are beyond repair.' },
];
const TONE_BG = {
  rose:   'bg-rose-50 border-rose-300 text-rose-900',
  sky:    'bg-sky-50 border-sky-300 text-sky-900',
  amber:  'bg-amber-50 border-amber-300 text-amber-900',
  teal: 'bg-teal-50 border-teal-300 text-teal-900',
};
const TONE_ACTIVE = {
  rose:   'bg-rose-600 border-rose-700 text-white shadow-md',
  sky:    'bg-sky-600 border-sky-700 text-white shadow-md',
  amber:  'bg-amber-600 border-amber-700 text-white shadow-md',
  teal: 'bg-teal-600 border-teal-700 text-white shadow-md',
};


/**
 * RestorationConcreteRepairs — Stage 1 sub-builder for Concrete
 * Restoration jobs with auto-save, undo, and per-pin repair config.
 *
 * Top of the form: a 4-option Repair Option picker (Full B&P / Full
 * Repoint / Part / Replace ALL) that auto-suggests from the
 * ridge_condition note captured during roof appraisal.
 *
 * Auto-save: debounced 1500ms after the last change. Manual Save and
 * Undo (10-deep server-side history) are always available.
 */
export default function RestorationConcreteRepairs({ api, appraisalId, subtype, onLinesChanged, onJumpToPin, onOpenAnnotator, paintHiddenByDefault = false, allowSeparateQuote = false }) {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);              // shared sections (tile/valley/aquaseal)
  const [scenarios, setScenarios] = useState([]);    // Option A / B / C …
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [preview, setPreview] = useState({ lines: [], total: 0, materials_total: 0, labour_total: 0, split_delta: 0 });
  // v10.29 Sprint 4b · Slice C (2026-02c) — Fast-jump navigator.
  const jumpToComponents = useCallback((componentType) => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', 'Components');
    params.set('focus_section', componentType || 'Ridge');
    navigate(`?${params.toString()}`, { replace: false });
  }, [navigate]);
  // v10.29 Sprint 4b · Slice H (2026-02c) — CalcShadow toggle.
  // Shared session storage key with Iron so both sub-tabs stay in sync.
  const [showCalcs, setShowCalcs] = useState(() => {
    try { return typeof sessionStorage !== 'undefined' && sessionStorage.getItem('repairs_show_calcs') === '1'; }
    catch { return false; }
  });
  const toggleCalcs = useCallback(() => {
    setShowCalcs((v) => {
      const next = !v;
      try { sessionStorage.setItem('repairs_show_calcs', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  // v10.29 Sprint 4b · Yellow banner (2026-02c) — per-appraisal dismiss
  // (shared key with Iron so dismissing on one sub-tab hides on both).
  const bannerKey = `repairs_banner_dismissed_${appraisalId}`;
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem(bannerKey) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { setBannerDismissed(typeof localStorage !== 'undefined' && localStorage.getItem(bannerKey) === '1'); }
    catch { /* ignore */ }
  }, [bannerKey]);
  const dismissBanner = useCallback(() => {
    try { localStorage.setItem(bannerKey, '1'); } catch { /* ignore */ }
    setBannerDismissed(true);
  }, [bannerKey]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [scenarioDirty, setScenarioDirty] = useState(false);
  const [autoSave, setAutoSave] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  // v10.20 — Repairs-context render (isolated Repairs Builder, not the
  // Restoration Repairs+Paint workflow) keeps Stage-2 Paint collapsed
  // by default; operator opts in via the reveal button. Mirrors the
  // pattern already used by RestorationIronRepairs.
  const [paintRevealed, setPaintRevealed] = useState(!paintHiddenByDefault);
  const saveTimer = useRef(null);
  const cfgRef = useRef(cfg);
  const scenariosRef = useRef(scenarios);
  const activeIdRef = useRef(activeScenarioId);
  cfgRef.current = cfg;
  scenariosRef.current = scenarios;
  activeIdRef.current = activeScenarioId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/restoration/appraisals/${appraisalId}/concrete-repairs/config`);
      setCfg(data.config);
      setMeta(data);
      const list = Array.isArray(data.scenarios) ? data.scenarios : [];
      setScenarios(list);
      // Default active = chosen, else first
      const chosenId = data.chosen_scenario_id || (list[0] && list[0].id) || null;
      setActiveScenarioId((cur) => cur && list.some(s => s.id === cur) ? cur : chosenId);
      setDirty(false);
      setScenarioDirty(false);
      // Preview is always of the CHOSEN scenario (what would push to Components).
      const { data: prev } = await api.get(`/restoration/appraisals/${appraisalId}/concrete-repairs/preview`);
      setPreview(prev);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load]);

  // Iter217 — Confirm/Unconfirm Concrete pins. Operates on raw_ids
  // (or null = whole section). For ridge/barge the backend mirrors
  // the flag across every scenario so the operator confirms a pin
  // once for all Options. Refetch on success so pending_counts +
  // priced totals refresh.
  //
  // Iter223 follow-up — apply an OPTIMISTIC patch to the local config
  // before the network round-trip so the amber confirm-required banner
  // clears instantly on click. The subsequent `load()` reconciles in
  // case the server rejected/overrode anything.
  const confirmPins = useCallback(async (section, rawIds, confirmed) => {
    // Optimistic local flip on every matching raw_id (or the whole section).
    setCfg((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const targetSections = section === 'all'
        ? ['ridge', 'barge', 'valley', 'aquaseal', 'tile']
        : [section];
      targetSections.forEach((s) => {
        const node = next[s];
        if (!node || !Array.isArray(node.items)) return;
        const items = node.items.map((it) => {
          if (rawIds && !rawIds.includes(it.raw_id)) return it;
          return { ...it, confirmed: !!confirmed };
        });
        next[s] = { ...node, items };
      });
      return next;
    });
    try {
      const { data: r } = await api.post(
        `/restoration/appraisals/${appraisalId}/concrete-repairs/confirm`,
        { section, raw_ids: rawIds, confirmed },
      );
      if (r?.warnings?.length) {
        toast.warning(`${confirmed ? 'Confirmed' : 'Unconfirmed'} ${r.affected} pin${r.affected === 1 ? '' : 's'}.`, {
          description: r.warnings.slice(0, 5).join(' · ') + (r.warnings.length > 5 ? ` · +${r.warnings.length - 5} more` : ''),
        });
      } else if (r?.affected > 0) {
        toast.success(`${confirmed ? 'Confirmed' : 'Unconfirmed'} ${r.affected} pin${r.affected === 1 ? '' : 's'}.`);
      }
      await load();
      if (onLinesChanged) await onLinesChanged();
    } catch (e) {
      // Revert the optimistic flip on failure so the UI stays honest.
      await load();
      toast.error(`Confirm failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [api, appraisalId, load, onLinesChanged]);

  const doSave = useCallback(async (silent = false) => {
    const current = cfgRef.current;
    const allScenarios = scenariosRef.current;
    const activeId = activeIdRef.current;
    if (!current && !allScenarios?.length) return;
    if (!silent) setSaving(true);
    setErr(null);
    try {
      // 1. Save shared sections (tile / valley / aquaseal) — only the bits the FE writes.
      if (current) {
        const sharedPayload = {
          tile:     current.tile     || null,
          valley:   current.valley   || null,
          aquaseal: current.aquaseal || null,
        };
        const res = await api.put(`/restoration/appraisals/${appraisalId}/concrete-repairs/shared`, sharedPayload);
        setMeta((m) => m ? { ...m, history_depth: res.data?.history_depth ?? m.history_depth } : m);
      }
      // 2. Save the active scenario's ridge + barge in a single PUT.
      const active = (allScenarios || []).find(s => s.id === activeId);
      if (active) {
        await api.put(`/restoration/appraisals/${appraisalId}/concrete-repairs/scenarios/${active.id}`, {
          name:               active.name,
          include_in_summary: active.include_in_summary,
          ridge:              active.ridge,
          barge:              active.barge,
        });
      }
      const { data: prev } = await api.get(`/restoration/appraisals/${appraisalId}/concrete-repairs/preview`);
      setPreview(prev);
      setDirty(false);
      setScenarioDirty(false);
      setLastSavedAt(Date.now());
      if (onLinesChanged) onLinesChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      if (!silent) setSaving(false);
    }
  }, [api, appraisalId, onLinesChanged]);

  // Debounced auto-save 1.5s after last change.
  useEffect(() => {
    if (!autoSave || (!dirty && !scenarioDirty)) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(true), 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [autoSave, dirty, scenarioDirty, cfg, scenarios, doSave]);

  const onUndo = async () => {
    if (!meta?.history_depth) return;
    if (!window.confirm('Restore the most recent saved snapshot? Your current unsaved changes will be lost.')) return;
    setSaving(true);
    setErr(null);
    try {
      await api.post(`/restoration/appraisals/${appraisalId}/concrete-repairs/undo`);
      await load();
      if (onLinesChanged) onLinesChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Patch a top-level shared section (tile / valley / aquaseal).
  const update = (patch) => { setCfg(c => ({ ...c, ...patch })); setDirty(true); };

  // Patch the ACTIVE scenario's ridge or barge.
  const updateActiveScenario = (patch) => {
    setScenarios((prev) => prev.map(s => s.id === activeScenarioId ? { ...s, ...patch } : s));
    setScenarioDirty(true);
  };

  // Scenario CRUD wrappers — call backend then re-load so the UI mirrors server state.
  const createScenario = async ({ from_id = null, full_repair_option = null } = {}) => {
    try {
      const { data } = await api.post(`/restoration/appraisals/${appraisalId}/concrete-repairs/scenarios`, {
        from_id, full_repair_option,
      });
      await load();
      if (data?.scenario?.id) setActiveScenarioId(data.scenario.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };
  const deleteScenario = async (scn) => {
    if (!window.confirm(`Delete "${scn.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/restoration/appraisals/${appraisalId}/concrete-repairs/scenarios/${scn.id}`);
      if (activeScenarioId === scn.id) setActiveScenarioId(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };
  const setChosen = async (scn) => {
    try {
      await api.post(`/restoration/appraisals/${appraisalId}/concrete-repairs/scenarios/${scn.id}/chosen`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };
  const renameScenario = (scnId, newName) => {
    setScenarios((prev) => prev.map(s => s.id === scnId ? { ...s, name: newName } : s));
    setScenarioDirty(true);
  };
  const toggleIncludeInSummary = (scnId) => {
    setScenarios((prev) => prev.map(s => s.id === scnId ? { ...s, include_in_summary: !s.include_in_summary } : s));
    setScenarioDirty(true);
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-slate-500 p-4">
      <Loader2 size={14} className="animate-spin" /> Loading Concrete Repairs config…
    </div>
  );
  if (err && !meta) return (
    <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-800" data-testid="concrete-repairs-load-error">
      <div className="font-semibold flex items-center gap-2"><AlertCircle size={14} /> Concrete Repairs builder failed to load</div>
      <div className="mt-1 text-xs">{err}</div>
      <button onClick={load} className="mt-2 text-xs border border-rose-300 bg-white text-rose-800 rounded px-2 py-1 hover:bg-rose-50 inline-flex items-center gap-1">
        <RefreshCw size={11} /> Retry
      </button>
    </div>
  );
  if (!meta) return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900" data-testid="concrete-repairs-no-meta">
      <div className="font-semibold flex items-center gap-2"><AlertCircle size={14} /> Concrete Repairs builder — no metadata returned</div>
      <div className="mt-1 text-xs">The /concrete-repairs/config endpoint returned no data for this appraisal. <button onClick={load} className="underline font-semibold">Retry</button>.</div>
    </div>
  );
  if (!meta.is_concrete) return (
    <div className="bg-white border border-slate-200 rounded-md p-4 text-sm text-slate-600" data-testid="concrete-repairs-not-applicable">
      <div className="font-semibold text-slate-800 flex items-center gap-2"><Layers size={14} /> Concrete Repairs scenarios</div>
      <p className="mt-1 text-xs text-slate-500">
        The multi-scenario Repair Option builder (Full B&amp;P / Repoint / Partial / Replace ALL) is available for <strong>Concrete-family roofs</strong> (Modern Concrete · Old Concrete · Clay). Switch the appraisal&rsquo;s Roof Subtype to a Concrete family value to use this builder.
      </p>
      <p className="mt-1 text-[11px] text-slate-400 italic">Current subtype: <strong>{subtype || meta.subtype || '— not set —'}</strong></p>
    </div>
  );
  if (err && !cfg) return (
    <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-800">
      <AlertCircle size={14} className="inline mr-1" /> {err}
    </div>
  );

  const activeScenario = scenarios.find(s => s.id === activeScenarioId) || scenarios[0] || null;
  const ridgeCfg = activeScenario?.ridge || {};
  const bargeCfg = activeScenario?.barge || {};
  const ridgeOption = (ridgeCfg.full_repair_option || 'part').toLowerCase();
  const totalRidgeLm = (ridgeCfg.treatments || []).reduce((s, t) => s + (parseFloat(t.length_m) || 0), 0);
  const totalBargeLm = (bargeCfg.treatments || []).reduce((s, t) => s + (parseFloat(t.length_m) || 0), 0);
  const isReplaceAllRidges = ((ridgeCfg.mode || '').toLowerCase() === 'replace_all') || ridgeOption === 'replace_all';

  // Iter 107 — rate-flavour Clone toggle. The 3 buttons swap the rate
  // lookup between Modern / Old / Clay without changing the appraisal's
  // stored roof_subtype. Clay aliases Old Concrete (per operator spec).
  // When the active flavour and the stored subtype disagree we show a
  // 1-click sync banner.
  const rateFlavour = meta?.rate_flavour || '';
  const effRateSubtype = meta?.effective_rate_subtype || subtype;
  const flavourMismatch = rateFlavour && rateFlavour !== subtype && !(
    rateFlavour === 'Clay' && (subtype === 'Clay Tile' || subtype === 'Clay')
  );
  const setRateFlavour = async (next) => {
    try {
      await api.patch(`/restoration/appraisals/${appraisalId}/concrete-repairs/rate-flavour`, { rate_flavour: next });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Flavour change failed');
    }
  };
  const syncSubtypeToFlavour = async () => {
    try {
      const targetSubtype = rateFlavour;
      await api.patch(`/appraisals/${appraisalId}/inline-field`, { field: 'roof_subtype', value: targetSubtype });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Subtype sync failed');
    }
  };

  return (
    <RateDetailProvider api={api} onSaved={async () => { await load(); if (onLinesChanged) await onLinesChanged(); }}>
    <div className="space-y-4" data-testid="concrete-repairs-section">
      {/* v10.29 Sprint 4b · Yellow banner (2026-02c) — Re-push overwrite reminder */}
      {!bannerDismissed && (
        <div className="px-3 py-2 rounded text-xs flex items-start gap-2 bg-amber-50 text-amber-900 ring-1 ring-amber-200"
             data-testid="repairs-repush-banner-concrete">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <strong>Heads-up:</strong> Re-pushing repairs overwrites any manual edits made to
            builder-emitted lines in the <span className="font-semibold">Components</span> tab.
          </div>
          <button className="text-[11px] underline hover:no-underline"
                  onClick={dismissBanner}
                  data-testid="repairs-repush-banner-dismiss-concrete">
            dismiss (this appraisal)
          </button>
        </div>
      )}
      {/* Iter217 — global Pending pins banner. Aggregates pending
          counts across the 4 pin-driven sections (ridge / barge /
          valley / aquaseal). Confirm-all-pending here flips every
          pending pin to confirmed in one shot. Hidden when zero. */}
      {(() => {
        const pc = meta?.pending_counts || {};
        const totalPending = (pc.ridge||0) + (pc.barge||0) + (pc.valley||0) + (pc.aquaseal||0);
        if (totalPending === 0) return null;
        return (
          <div className="bg-amber-50 border border-amber-300 rounded-md px-4 py-2.5 flex items-center justify-between gap-3" data-testid="concrete-pending-banner">
            <div className="text-xs text-amber-900">
              <strong>⚠ {totalPending} pending pin{totalPending === 1 ? '' : 's'}</strong> waiting for review — excluded from priced totals until confirmed.
              <span className="text-amber-700 ml-2">
                {Object.entries(pc).filter(([, n]) => n > 0).map(([s, n]) => `${s} ${n}`).join(' · ')}
              </span>
            </div>
            <button
              type="button"
              data-testid="concrete-confirm-all-pending"
              onClick={() => confirmPins('all', null, true)}
              className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold"
            >✓ Confirm all {totalPending} pending</button>
          </div>
        );
      })()}
      <div className="bg-gradient-to-br from-sky-50 to-indigo-50 border border-sky-200 rounded-md p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-bold text-sky-900 flex items-center gap-2 font-[Manrope]">
              <Layers size={16} /> Stage 1 — Full Repairs · {effRateSubtype || subtype}
            </h4>
            <p className="text-xs text-sky-800/80 mt-1 max-w-3xl">
              Pick the Repair Option below — the rest of the form adapts. Ridging quantities come from the annotated <strong>Master Roof Plan</strong>. Tile spec mirrors the existing <strong>Roof Details</strong>.
            </p>
            {/* Clone toggle — flips the rate flavour without touching the appraisal's subtype. */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-bold text-sky-700">Rate flavour</span>
              <div className="inline-flex bg-white border border-sky-300 rounded p-0.5 shadow-sm" data-testid="concrete-rate-flavour-toggle">
                {['Modern Concrete', 'Old Concrete', 'Clay'].map(f => {
                  const active = (rateFlavour || subtype) === f || (rateFlavour === '' && f === subtype) ||
                    (f === 'Clay' && (rateFlavour === '' && (subtype === 'Clay Tile' || subtype === 'Clay')));
                  return (
                    <button
                      key={f}
                      type="button"
                      data-testid={`concrete-rate-flavour-${f.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setRateFlavour(active && rateFlavour ? '' : f)}
                      title={active && rateFlavour ? `Currently active · click to clear (fall back to stored subtype "${subtype}")` : `Switch rate lookup to ${f}`}
                      className={`px-2.5 py-1 text-xs font-semibold rounded transition-all ${
                        active ? 'bg-sky-600 text-white shadow-sm' : 'text-sky-700 hover:bg-sky-50'
                      }`}
                    >
                      {f}{f === 'Clay' && <span className="ml-1 text-[9px] opacity-70">(= Old)</span>}
                    </button>
                  );
                })}
              </div>
              {rateFlavour && (
                <button type="button" data-testid="concrete-rate-flavour-clear"
                        onClick={() => setRateFlavour('')}
                        className="text-[10px] text-slate-500 hover:text-sky-700 underline">
                  Clear override
                </button>
              )}
            </div>
            {flavourMismatch && (
              <div data-testid="concrete-rate-flavour-mismatch"
                   className="mt-2 flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-[11px] text-amber-900">
                <span>
                  Rates: <strong>{effRateSubtype}</strong> · Subtype: <strong>{subtype || '—'}</strong> — these don't match.
                </span>
                <button type="button"
                        data-testid="concrete-rate-flavour-sync-subtype"
                        onClick={syncSubtypeToFlavour}
                        className="px-2 py-0.5 text-[10px] font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded">
                  Save subtype to match
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="text-xs text-slate-500">Concrete repairs sub-total</div>
            <div className="text-2xl font-bold text-sky-800 font-mono-num">${preview.total.toFixed(2)}</div>
            {/* v10.29 Sprint 4b · Slice A (2026-02c) — Materials/Labour split */}
            {(preview.materials_total || preview.labour_total) > 0 && (
              <div className="flex flex-wrap justify-end gap-1" data-testid="concrete-mat-lab-split">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200 text-slate-600">
                  <span className="font-semibold">Mat:</span> <span className="font-mono-num">${(preview.materials_total || 0).toFixed(2)}</span>
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200 text-slate-600">
                  <span className="font-semibold">Lab:</span> <span className="font-mono-num">${(preview.labour_total || 0).toFixed(2)}</span>
                </span>
                {Math.abs(preview.split_delta || 0) >= 0.02 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 ring-1 ring-amber-300 text-amber-800"
                        title="Some lines don't include a mat/lab split — Δ = total − (mat + lab)"
                        data-testid="concrete-split-delta-warn">
                    Δ ${(preview.split_delta || 0).toFixed(2)}
                  </span>
                )}
              </div>
            )}
            <SaveStatusBar
              autoSave={autoSave}
              setAutoSave={setAutoSave}
              dirty={dirty || scenarioDirty}
              saving={saving}
              lastSavedAt={lastSavedAt}
              historyDepth={meta?.history_depth || 0}
              onSave={() => doSave(false)}
              onUndo={onUndo}
              onRefresh={load}
            />
          </div>
        </div>

        {/* Inline dropdown row — every field that drives this quote.
            Each <select> PATCHes /appraisals/{id}/inline-field then
            re-pulls the config so dependent rates refresh. */}
        <HeaderDropdownsRow
          api={api}
          appraisalId={appraisalId}
          sources={meta.header_sources || {}}
          values={meta.header_values || {}}
          ridgeCaps={meta.ridge_caps || []}
          onChanged={load}
        />
      </div>

      {/* ─── Scenario sidebar (left) + active-scenario canvas (right) ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4" data-testid="scenario-grid">
        {/* LEFT — scenario list */}
        <div className="space-y-2" data-testid="scenario-list">
          {scenarios.map((s) => (
            <ScenarioListCard
              key={s.id}
              scenario={s}
              subtype={subtype}
              snapshot={(meta?.scenario_totals || {})[s.id] || {}}
              active={s.id === activeScenarioId}
              onSelect={() => setActiveScenarioId(s.id)}
              onToggleChosen={() => setChosen(s)}
              onToggleSummary={() => toggleIncludeInSummary(s.id)}
              onClone={() => createScenario({ from_id: s.id, full_repair_option: null })}
              onDelete={() => deleteScenario(s)}
              onPrint={() => window.open(`${api.defaults.baseURL}/restoration/appraisals/${appraisalId}/quote.pdf?scenario_id=${s.id}`, '_blank')}
              onRefresh={load}
              disableDelete={scenarios.length <= 1}
            />
          ))}
          <button
            type="button"
            onClick={() => createScenario({})}
            data-testid="scenario-add-blank-btn"
            className="w-full text-xs px-3 py-2 border-2 border-dashed border-slate-300 rounded text-slate-600 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50/40 transition-all flex items-center justify-center gap-1.5"
          >+ New blank option</button>
        </div>

        {/* RIGHT — active scenario canvas */}
        <div className="space-y-3 min-w-0" data-testid="scenario-canvas">
          {activeScenario ? (
            <>
              {/* Name input + Chosen pill + auto-save chip */}
              <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
                <input
                  type="text"
                  value={activeScenario.name}
                  onChange={(e) => renameScenario(activeScenario.id, e.target.value)}
                  data-testid={`scenario-${activeScenario.letter}-name`}
                  className="flex-1 min-w-[14rem] px-3 py-1.5 text-base font-semibold text-slate-900 border border-slate-300 rounded"
                />
                {activeScenario.is_chosen ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1">
                    <Star size={12} fill="currentColor" /> Chosen quote
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setChosen(activeScenario)}
                    data-testid={`scenario-${activeScenario.letter}-mark-chosen`}
                    className="text-xs font-semibold text-slate-600 hover:text-amber-700 inline-flex items-center gap-1 border border-slate-300 hover:border-amber-400 rounded-full px-2.5 py-1"
                    title="Mark this option as the headline (chosen) quote"
                  ><Star size={12} /> Mark as chosen</button>
                )}
                <span className="text-[11px] text-slate-500 font-mono">Option {activeScenario.letter}{activeScenario.is_chosen ? ' ★ chosen' : ''}</span>
                {allowSeparateQuote && (
                  <MakeSeparateQuoteButton api={api} appraisalId={appraisalId} body={{ scenario_id: activeScenario.id }}
                    label={activeScenario.name} testid="repairs-make-separate-quote" />
                )}
              </div>

              {/* "Clone as:" quick-action bar — one-click clones of the
                  active scenario into other repair-option presets. The
                  current option is filtered out so the buttons always
                  show the alternative paths the operator can offer.
                  Also carries the per-scenario "Replace ALL Ridges"
                  toggle on the right — moved here (and the standalone
                  Repair Option card removed) per operator request
                  Feb '26: the Clone-as variants ARE the repair
                  options, so the duplicate picker just added noise. */}
              <CloneAsVariantBar
                activeOption={ridgeOption}
                onCloneAs={(opt) => createScenario({ from_id: activeScenario.id, full_repair_option: opt })}
                isReplaceAllRidges={isReplaceAllRidges}
                onToggleReplaceAll={(checked) => {
                  const next = checked
                    ? { ...ridgeCfg, mode: 'replace_all', full_repair_option: 'replace_all' }
                    : { ...ridgeCfg, mode: 'keep', full_repair_option: (ridgeCfg.full_repair_option === 'replace_all' ? 'full_bp' : ridgeCfg.full_repair_option) };
                  updateActiveScenario({ ridge: next });
                }}
                replaceAllTestId={`scenario-${activeScenario.letter}-replace-all-toggle`}
              />

              {/* Suggestion banner from ridge_condition note */}
              {meta?.suggestion?.suggestion && (
                <SuggestionBanner
                  suggestion={meta.suggestion}
                  ridgeCondition={meta.ridge_condition}
                  currentOption={ridgeOption}
                  onApply={() => updateActiveScenario({ ridge: { ...ridgeCfg, full_repair_option: meta.suggestion.suggestion, mode: meta.suggestion.suggestion === 'replace_all' ? 'replace_all' : 'keep' } })}
                  api={api}
                  appraisalId={appraisalId}
                  fieldKey="ridge_condition"
                  fieldLabel="Ridge Condition"
                  onConditionSaved={load}
                />
              )}

              {/* Ridges + Barges for the ACTIVE scenario. The standalone
                  Repair Option picker card has been removed — its
                  function is fully covered by:
                    1. The active scenario's preset (set on creation
                       via Clone-as-variant), and
                    2. The Replace ALL Ridges toggle, which now lives
                       on the right of the Clone-as-variant bar. */}
              <RidgeSection
                title="Ridges"
                kindKey="ridge"
                pinKind="Ridge"
                icon={<Triangle size={14} />}
                cfg={ridgeCfg}
                caps={meta.ridge_caps}
                rates={meta.ridge_treatments}
                totalLm={totalRidgeLm}
                pendingCount={meta?.pending_counts?.ridge || 0}
                onChange={(next) => updateActiveScenario({ ridge: next })}
                onJumpToPin={onJumpToPin}
                onOpenAnnotator={onOpenAnnotator}
                onConfirmPins={(rawIds, confirmed) => confirmPins('ridge', rawIds, confirmed)}
              />
              <RidgeSection
                title="Barges"
                kindKey="barge"
                pinKind="Barge"
                icon={<GripHorizontal size={14} />}
                cfg={bargeCfg}
                caps={meta.ridge_caps}
                rates={meta.barge_treatments || meta.ridge_treatments}
                totalLm={totalBargeLm}
                pendingCount={meta?.pending_counts?.barge || 0}
                onChange={(next) => updateActiveScenario({ barge: next })}
                onJumpToPin={onJumpToPin}
                onOpenAnnotator={onOpenAnnotator}
                onConfirmPins={(rawIds, confirmed) => confirmPins('barge', rawIds, confirmed)}
              />
            </>
          ) : (
            <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">
              Select an option on the left to edit, or click <strong>+ New blank option</strong> to start.
            </div>
          )}
        </div>
      </div>

      {/* ─── Shared sections (apply to every scenario) ─── */}
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 pt-2 flex items-center gap-2">
        <span className="h-px flex-1 bg-slate-200" />
        Shared across every option (same physical roof)
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      <TileSection
        cfg={cfg.tile}
        roofDetails={meta.roof_details}
        tileRate={meta.tile_rate}
        appraisalQty={meta.appraisal_tile_defect_qty}
        onChange={(next) => update({ tile: next })}
      />

      <ValleySection
        cfg={cfg.valley}
        options={meta.valley_options}
        pendingCount={meta?.pending_counts?.valley || 0}
        onChange={(next) => update({ valley: next })}
        onJumpToPin={onJumpToPin}
        onOpenAnnotator={onOpenAnnotator}
        onConfirmPins={(rawIds, confirmed) => confirmPins('valley', rawIds, confirmed)}
      />

      <AquasealSection
        cfg={cfg.aquaseal}
        options={meta.aquaseal_options}
        pendingCount={meta?.pending_counts?.aquaseal || 0}
        onChange={(next) => update({ aquaseal: next })}
        onJumpToPin={onJumpToPin}
        onOpenAnnotator={onOpenAnnotator}
        onConfirmPins={(rawIds, confirmed) => confirmPins('aquaseal', rawIds, confirmed)}
      />

      {appraisalId && (
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <ComponentsFlashingEmbed api={api} appraisalId={appraisalId} flashingOnly testid="concrete-components-flashing" />
        </div>
      )}

      {/* v10.20 — Stage-2 Paint section is gated behind a reveal
          button when the parent flags a pure Repairs context so the
          Paint Systems form doesn't leak into a Repairs-only quote.
          Once opened it stays expanded. */}
      {paintHiddenByDefault && !paintRevealed ? (
        <button
          type="button"
          data-testid="concrete-repairs-reveal-paint"
          onClick={() => setPaintRevealed(true)}
          className="w-full px-4 py-3 border-2 border-dashed border-emerald-300 bg-emerald-50/40 hover:bg-emerald-50 text-emerald-800 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
          title="Open the Stage 2 — Paint Systems picker for this Repairs job"
        >
          🎨 + Select painting info
          <span className="text-[11px] font-normal text-emerald-700/70 ml-1">(Stage 2 paint hidden by default for Repairs)</span>
        </button>
      ) : (
        <Stage2PaintSection
          api={api}
          appraisalId={appraisalId}
          onLinesChanged={async () => {
            const { data: prev } = await api.get(`/restoration/appraisals/${appraisalId}/concrete-repairs/preview`);
            setPreview(prev);
            if (onLinesChanged) onLinesChanged();
          }}
        />
      )}

      {/* Calculated line preview — toggle Stage 1 / Stage 2 / All. */}
      <CalculatedLinesPanel
        preview={preview}
        autoSave={autoSave}
        showCalcs={showCalcs}
        toggleCalcs={toggleCalcs}
        jumpToComponents={jumpToComponents}
      />
    </div>
    </RateDetailProvider>
  );
}


/**
 * Calculated lines panel — renders every line item produced by the
 * builder with a leading STAGE column, plus a filter toggle so the
 * operator can isolate Stage 1 repairs from Stage 2 paint and a
 * footer showing Stage 1 / Stage 2 sub-totals + grand total.
 */
function CalculatedLinesPanel({ preview, autoSave, showCalcs = false, toggleCalcs = () => {}, jumpToComponents = () => {} }) {
  const [filter, setFilter] = useState('all');  // 'all' | 'stage1' | 'stage2'
  const lines = preview.lines || [];
  const stageTotals = preview.stage_totals || { stage_1: 0, stage_2: 0 };
  const stageCounts = preview.stage_counts || {
    stage_1: lines.filter(l => (l.stage || 1) === 1).length,
    stage_2: lines.filter(l => (l.stage || 1) === 2).length,
  };
  const visible = lines.filter(l => {
    const s = Number(l.stage || 1);
    if (filter === 'stage1') return s === 1;
    if (filter === 'stage2') return s === 2;
    return true;
  });
  const visibleTotal = visible.reduce((s, l) => s + (Number(l.total) || 0), 0);
  return (
    <details className="bg-white border border-slate-200 rounded-md" open data-testid="calculated-lines-panel">
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
        <Wrench size={13} /> Calculated lines <span className="text-xs font-normal text-slate-500">({lines.length})</span>
      </summary>
      <div className="px-4 pb-3 space-y-2">
        {/* Stage filter toggle */}
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1.5">
          <div className="inline-flex bg-slate-100 rounded p-0.5" role="tablist" data-testid="calc-lines-stage-filter">
            {[
              { v: 'all',    label: `All (${lines.length})`,              testid: 'filter-all' },
              { v: 'stage1', label: `Stage 1 — Repairs (${stageCounts.stage_1})`, testid: 'filter-stage1' },
              { v: 'stage2', label: `Stage 2 — Paint (${stageCounts.stage_2})`,   testid: 'filter-stage2' },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                data-testid={`calc-lines-${opt.testid}`}
                onClick={() => setFilter(opt.v)}
                className={`px-3 py-1 text-xs font-semibold rounded transition-all ${
                  filter === opt.v
                    ? (opt.v === 'stage2' ? 'bg-emerald-600 text-white shadow-sm' : opt.v === 'stage1' ? 'bg-sky-600 text-white shadow-sm' : 'bg-slate-700 text-white shadow-sm')
                    : 'text-slate-600 hover:bg-white'
                }`}
              >{opt.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div data-testid="stage-total-1">
              <span className="text-slate-500">Stage 1</span>{' '}
              <span className="font-mono-num font-semibold text-sky-800">${stageTotals.stage_1.toFixed(2)}</span>
            </div>
            <div data-testid="stage-total-2">
              <span className="text-slate-500">Stage 2</span>{' '}
              <span className="font-mono-num font-semibold text-emerald-800">${stageTotals.stage_2.toFixed(2)}</span>
            </div>
            <div data-testid="stage-total-grand">
              <span className="text-slate-500">Total</span>{' '}
              <span className="font-mono-num font-bold text-slate-900">${preview.total.toFixed(2)}</span>
            </div>
            {/* v10.29 Sprint 4b · Slice H — CalcShadow toggle */}
            <button type="button"
                    onClick={toggleCalcs}
                    data-testid="concrete-calcshadow-toggle"
                    className={`text-[10px] px-2 py-0.5 rounded border ${showCalcs ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    title="Toggle calculation breakdown per line">
              {showCalcs ? '✓ calcs' : 'calcs'}
            </button>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="text-xs text-slate-500 py-2 italic">
            {lines.length === 0
              ? `No repair lines yet — pick treatments above. ${autoSave ? 'Changes save automatically.' : 'Click Save to refresh.'}`
              : `No lines match this filter — switch to "All" to see the rest.`}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-1.5 w-16">Stage</th>
                <th className="text-left">Item</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Total</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((l, i) => {
                const s = Number(l.stage || 1);
                const stagePill = s === 2
                  ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid={`line-${i}-stage`}>Stage 2</span>
                  : <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-200" data-testid={`line-${i}-stage`}>Stage 1</span>;
                return (
                  <tr key={i} className="border-b border-slate-100 last:border-0 align-top" data-testid={`calc-line-row-${i}`}>
                    <td className="py-1.5">{stagePill}</td>
                    <td className="py-1.5">
                      <div className="font-medium text-slate-800" data-testid={`concrete-line-item-${i}`}>{l.type_name}</div>
                      {l.notes && <div className="text-[11px] text-slate-400 italic">{l.notes}</div>}
                      {/* v10.29 Sprint 4b · Slice H (2026-02c) — CalcShadow toggleable */}
                      {showCalcs && (
                        <div className="text-[10px] text-slate-500 italic font-mono-num mt-0.5"
                             data-testid={`concrete-line-calc-${i}`}>
                          {l.quantity} {l.unit} × ${(l.rate || 0).toFixed(2)} ={' '}
                          <strong className="text-slate-700">${(l.total || 0).toFixed(2)}</strong>
                          {(l.materials_amount || l.labour_amount) > 0 && (
                            <span className="ml-2">
                              (M ${(l.materials_amount || 0).toFixed(2)} · L ${(l.labour_amount || 0).toFixed(2)})
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-right font-mono-num">{l.quantity} {l.unit}</td>
                    <td className="text-right font-mono-num text-slate-600">${l.rate.toFixed(2)}</td>
                    <td className="text-right font-mono-num font-semibold text-slate-800">${l.total.toFixed(2)}</td>
                    <td className="text-right">
                      {/* v10.29 Sprint 4b · Slice C — fast-jump */}
                      <button type="button"
                              onClick={() => jumpToComponents(l.component_type)}
                              className="w-5 h-5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                              title={`Open ${l.component_type} section in Components tab`}
                              data-testid={`concrete-line-jump-${i}`}>
                        ↗
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filter !== 'all' && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-bold">
                  <td colSpan={4} className="py-2 text-right text-slate-700">
                    {filter === 'stage1' ? 'Stage 1 — Repairs sub-total' : 'Stage 2 — Paint sub-total'}
                  </td>
                  <td className="py-2 text-right font-mono-num text-slate-900" data-testid="filtered-subtotal">${visibleTotal.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </details>
  );
}


// ── Scenario list card (left sidebar, one per scenario) ──────────────

/**
 * Re-roof Builder–styled scenario card. Shows star/chosen toggle,
 * scenario name + key spec line (subtype · repair option) + quote no,
 * grand total and line count, then a footer row with "In summary"
 * checkbox plus refresh / print / clone / delete icon buttons. Click
 * anywhere on the body to make this scenario active.
 */
function ScenarioListCard({ scenario, subtype, snapshot, active, onSelect, onToggleChosen, onToggleSummary, onClone, onDelete, onPrint, onRefresh, disableDelete }) {
  const optionLabel = (() => {
    const o = (snapshot.ridge_option || (scenario.ridge || {}).full_repair_option || 'part').toLowerCase();
    const mode = (snapshot.ridge_mode || (scenario.ridge || {}).mode || 'keep').toLowerCase();
    if (mode === 'replace_all' || o === 'replace_all') return 'Replace ALL Ridging';
    return (REPAIR_OPTIONS.find(x => x.key === o) || {}).title || o;
  })();
  const total = Number(snapshot.total || 0);
  const lineCount = Number(snapshot.line_count || 0);
  return (
    <div
      onClick={onSelect}
      data-testid={`scenario-card-${scenario.letter}`}
      className={`p-3 rounded ring-1 cursor-pointer transition-all ${active ? 'ring-[#D35400] bg-orange-50 shadow-sm' : 'ring-slate-200 bg-white hover:ring-slate-300'}`}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleChosen(); }}
          className={`mt-0.5 ${scenario.is_chosen ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
          title={scenario.is_chosen ? 'Chosen headline quote' : 'Mark as chosen'}
          data-testid={`scenario-${scenario.letter}-chosen-toggle`}
        >
          <Star size={16} fill={scenario.is_chosen ? 'currentColor' : 'none'} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-[#0F172A] truncate" title={scenario.name}>{scenario.name}</div>
          <div className="text-xs text-slate-600 mt-0.5 truncate">
            {subtype || '—'} · {optionLabel}
          </div>
          <div className="text-xs text-slate-500 mt-1 font-mono-num">
            ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {lineCount} line{lineCount === 1 ? '' : 's'}
          </div>
          <div className="text-[10px] text-slate-400 font-mono mt-0.5">Option {scenario.letter}</div>
        </div>
      </div>
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-200">
        <label
          className="text-[11px] inline-flex items-center gap-1 cursor-pointer text-slate-600"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!scenario.include_in_summary}
            onChange={onToggleSummary}
            data-testid={`scenario-${scenario.letter}-summary-toggle`}
          /> In summary
        </label>
        <button
          onClick={(e) => { e.stopPropagation(); onRefresh && onRefresh(); }}
          className="ml-auto text-sky-500 hover:text-sky-700"
          title="Refresh totals from the server"
          data-testid={`scenario-${scenario.letter}-refresh`}
        ><RefreshCw size={13} /></button>
        <button
          onClick={(e) => { e.stopPropagation(); onPrint && onPrint(); }}
          className="text-slate-400 hover:text-slate-700"
          title="Print this option as a customer quote PDF"
          data-testid={`scenario-${scenario.letter}-print`}
        ><Printer size={13} /></button>
        <button
          onClick={(e) => { e.stopPropagation(); onClone(); }}
          className="text-slate-400 hover:text-slate-700"
          title="Clone (verbatim copy of this option)"
          data-testid={`scenario-${scenario.letter}-clone`}
        ><Copy size={13} /></button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          disabled={disableDelete}
          className="text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed"
          title={disableDelete ? 'Cannot delete the last option' : 'Delete this option'}
          data-testid={`scenario-${scenario.letter}-delete`}
        ><Trash2 size={13} /></button>
      </div>
    </div>
  );
}


// ── "Clone as variant in:" quick-action bar ───────────────────────────

/**
 * Horizontal quick-bar above the active scenario's editor (matches
 * the Re-roof Builder's "Clone as variant in: Galv / Zinc / Maxx"
 * pattern). For Restoration these are repair-option variants
 * (Repoint, Part B&P + Repoint, Replace All Ridging). Filters out
 * whichever option is currently active so the buttons always offer
 * alternatives the operator can spin up with one click.
 */
function CloneAsVariantBar({ activeOption, onCloneAs, isReplaceAllRidges, onToggleReplaceAll, replaceAllTestId }) {
  const current = (activeOption || 'part').toLowerCase();
  const variants = REPAIR_OPTIONS.filter(o => o.key !== current && o.key !== 'replace_all');  // Replace ALL = toggle on the right
  return (
    <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-md p-2.5" data-testid="scenario-clone-as-bar">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Clone as variant:</span>
      {variants.map((v) => (
        <button
          key={v.key}
          type="button"
          onClick={() => onCloneAs(v.key)}
          data-testid={`scenario-clone-as-${v.key}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-white hover:bg-slate-50 ring-1 ring-slate-300 rounded font-semibold text-slate-700"
          title={`Create a new option identical to this one but configured for ${v.title}`}
        >
          <Copy size={11} /> {v.title}
        </button>
      ))}
      {/* Right-aligned per-scenario "Replace ALL Ridges" toggle —
          moved here from the deleted Repair Option card. ml-auto
          pushes it to the right edge of the same row when the
          viewport is wide enough; on narrow screens the flex-wrap
          drops it below. */}
      {onToggleReplaceAll && (
        <label
          className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer"
          title="When ON, total ridging length is divided by cap length to produce a brand-new ridge replacement quote — overrides the B&P/Repoint preset."
        >
          <input
            type="checkbox"
            data-testid={replaceAllTestId}
            checked={!!isReplaceAllRidges}
            onChange={(e) => onToggleReplaceAll(e.target.checked)}
            className="rounded"
          />
          <span>Replace ALL Ridges (this option)</span>
        </label>
      )}
    </div>
  );
}


// ── Save / Undo / Auto-save status bar ────────────────────────────────

function SaveStatusBar({ autoSave, setAutoSave, dirty, saving, lastSavedAt, historyDepth, onSave, onUndo, onRefresh }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);
  const sinceText = lastSavedAt ? relTime(now - lastSavedAt) : 'never saved';
  return (
    <div className="flex items-center gap-1.5">
      <label className="text-[10px] font-semibold text-slate-600 flex items-center gap-1" title="Auto-save changes 1.5s after the last edit">
        <input
          type="checkbox"
          data-testid="concrete-repairs-autosave-toggle"
          checked={autoSave}
          onChange={(e) => setAutoSave(e.target.checked)}
          className="rounded"
        />
        Auto-save
      </label>
      <span data-testid="concrete-repairs-save-status" className={`text-[10px] px-2 py-1 rounded-full font-semibold ${saving ? 'bg-amber-100 text-amber-700' : dirty ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-800'}`}>
        {saving ? <><Loader2 size={9} className="inline animate-spin mr-0.5" /> Saving…</> : dirty ? 'Unsaved' : `Saved · ${sinceText}`}
      </span>
      <button
        data-testid="concrete-repairs-undo-btn"
        onClick={onUndo}
        disabled={historyDepth === 0}
        className="text-xs px-2 py-1 border border-slate-300 rounded bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
        title={historyDepth ? `Restore previous saved snapshot (${historyDepth} in stack)` : 'No history yet'}
      ><Undo2 size={11} /> Undo<span className="opacity-60">({historyDepth})</span></button>
      <button
        data-testid="concrete-repairs-refresh-btn"
        onClick={onRefresh}
        className="text-xs px-2 py-1 border border-sky-300 rounded bg-white text-sky-800 hover:bg-sky-100 flex items-center gap-1"
      ><RefreshCw size={11} /> Refresh</button>
      <button
        data-testid="concrete-repairs-save-btn"
        onClick={onSave}
        disabled={!dirty || saving}
        className={`text-xs px-3 py-1 rounded font-semibold flex items-center gap-1 ${dirty ? 'bg-[#D35400] text-white hover:bg-[#A04000]' : 'bg-slate-200 text-slate-500 cursor-not-allowed'}`}
      >{saving ? <><Loader2 size={11} className="animate-spin" /> Saving…</> : <><Save size={11} /> Save now</>}</button>
    </div>
  );
}

function relTime(ms) {
  const s = Math.round(ms / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}


// ── Suggestion banner from ridge_condition note ───────────────────────

function SuggestionBanner({ suggestion, ridgeCondition, currentOption, onApply,
                            api, appraisalId, fieldKey = 'ridge_condition',
                            fieldLabel = 'Ridge Condition', onConditionSaved }) {
  const opt = REPAIR_OPTIONS.find(o => o.key === suggestion.suggestion);
  const alreadyApplied = currentOption === suggestion.suggestion;
  const [editing, setEditing] = useState(false);
  return (
    <div className={`border rounded-md p-3 text-xs ${TONE_BG[opt?.tone || 'amber']}`} data-testid="repair-option-suggestion">
      <div className="flex items-start gap-2">
        <Sparkles size={14} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-bold">
            Auto-suggested: <span className="underline">{opt?.title || suggestion.suggestion}</span>
            {alreadyApplied && <span className="ml-2 text-[10px] font-semibold opacity-80">· APPLIED</span>}
          </div>
          <div className="opacity-90 mt-0.5"><strong>Why:</strong> {suggestion.reason}</div>
          {ridgeCondition && (
            <details className="mt-1">
              <summary className="cursor-pointer underline text-[10px] inline-flex items-center gap-2">
                Show full {fieldKey} note
                {api && appraisalId && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setEditing(true); }}
                    data-testid={`repair-option-suggestion-edit-${fieldKey}`}
                    title={`Open the Appraisal Form ${fieldLabel} field for editing`}
                    className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border border-current bg-white/60 hover:bg-white inline-flex items-center gap-1 no-underline"
                  >
                    <Pencil size={9} /> Edit {fieldLabel}
                  </button>
                )}
              </summary>
              <div className="text-[11px] italic mt-1 opacity-80">{ridgeCondition.slice(0, 600)}{ridgeCondition.length > 600 ? '…' : ''}</div>
            </details>
          )}
          {!ridgeCondition && api && appraisalId && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid={`repair-option-suggestion-edit-${fieldKey}`}
              className="mt-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border border-current bg-white/60 hover:bg-white inline-flex items-center gap-1"
            >
              <Pencil size={9} /> Add {fieldLabel} note
            </button>
          )}
        </div>
        {!alreadyApplied && (
          <button
            data-testid="repair-option-suggestion-apply"
            onClick={onApply}
            className="text-xs px-3 py-1 rounded-full bg-white border border-current font-semibold hover:bg-current hover:text-white transition-all whitespace-nowrap"
          >Apply</button>
        )}
      </div>
      {editing && api && appraisalId && (
        <AppraisalFieldEditorModal
          api={api}
          appraisalId={appraisalId}
          fieldKey={fieldKey}
          fieldLabel={fieldLabel}
          initialValue={ridgeCondition || ''}
          onClose={() => setEditing(false)}
          onSaved={async (newValue) => {
            setEditing(false);
            if (onConditionSaved) await onConditionSaved(newValue);
          }}
        />
      )}
    </div>
  );
}


/**
 * Iter 119 — Inline editor modal for a single Appraisal Form field
 * (e.g. ridge_condition / tile_condition / valley_condition). Opens
 * from the auto-suggestion banner so the appraiser can refine the
 * narrative note that drives the suggestion without leaving the
 * builder. Saves via `PATCH /appraisals/{id}/inline-field` (the
 * same whitelist already used by the Roof Appraisal form).
 */
function AppraisalFieldEditorModal({ api, appraisalId, fieldKey, fieldLabel,
                                      initialValue, onClose, onSaved }) {
  const [value, setValue] = useState(initialValue || '');
  const [busy, setBusy]   = useState(false);
  const [err,  setErr]    = useState(null);
  const dirty = value !== (initialValue || '');
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      // Iter 119 — use the dedicated `/conditions` PATCH endpoint
      // which whitelists all narrative + defect-count fields (roof
      // condition / per-area condition / per-area defect qty). Falls
      // back to /inline-field for any non-narrative key the caller
      // may wire in the future.
      const NARRATIVE_FIELDS = new Set([
        'roof_condition', 'roof_requirements', 'appraisal_condition',
        'appraisal_comments', 'appraisal_action_general',
        'ridge_condition', 'tile_condition', 'valley_condition',
        'barge_condition', 'sheet_condition', 'spouting_condition',
        'fastening_condition', 'flashing_condition',
      ]);
      if (NARRATIVE_FIELDS.has(fieldKey)) {
        await api.patch(`/appraisals/${appraisalId}/conditions`, {
          [fieldKey]: value,
        });
      } else {
        await api.patch(`/appraisals/${appraisalId}/inline-field`, {
          field: fieldKey,
          value,
        });
      }
      if (onSaved) await onSaved(value);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={() => onClose()}
         data-testid={`appraisal-field-editor-modal-${fieldKey}`}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-rose-50 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Appraisal form field · {fieldKey}</div>
            <h3 className="text-lg font-bold text-slate-900 mt-0.5">Edit {fieldLabel}</h3>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Updating this note re-runs the auto-suggestion based on the new keywords.
            </div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid={`appraisal-field-editor-close-${fieldKey}`}
                  className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          <label className="block">
            <span className="text-[10px] uppercase font-bold tracking-wide text-slate-500">{fieldLabel} note</span>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              rows={8}
              placeholder={`e.g. Existing Ridges fitted badly, some loose ridging and not straight and level. 1 hip using old Concrete ridge caps instead of Clay.`}
              data-testid={`appraisal-field-editor-textarea-${fieldKey}`}
              className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-rose-400 focus:border-rose-400"
            />
          </label>
          {err && (
            <div data-testid={`appraisal-field-editor-error-${fieldKey}`} className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              {err}
            </div>
          )}
          <div className="mt-2 text-[10px] text-slate-500">
            {value.length} characters · {value.split(/\s+/).filter(Boolean).length} words
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
                  data-testid={`appraisal-field-editor-cancel-${fieldKey}`}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded">Cancel</button>
          <button type="button" onClick={save} disabled={busy || !dirty}
                  data-testid={`appraisal-field-editor-save-${fieldKey}`}
                  className="px-4 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            {busy ? 'Saving…' : 'Save & refresh suggestion'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Ridge / Barge section (same shape) ────────────────────────────────

function RidgeSection({ title, kindKey, pinKind, icon, cfg, caps, rates, totalLm, pendingCount = 0, onChange, onJumpToPin, onOpenAnnotator, onConfirmPins }) {
  if (!cfg) return null;
  const treatments = cfg.treatments || [];
  const option = (cfg.full_repair_option || 'part').toLowerCase();
  const updateTreatment = (idx, patch) => {
    const next = [...treatments];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...cfg, treatments: next });
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid={`${kindKey}-section`}>
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 flex-wrap gap-2">
        <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
          {icon} {title} <span className="text-xs text-slate-500">({treatments.length} pin{treatments.length === 1 ? '' : 's'} · {totalLm.toFixed(1)}m total)</span>
          {pendingCount > 0 && (
            <span data-testid={`${kindKey}-pending-badge`} className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded ring-1 ring-amber-300">⚠ {pendingCount} pending</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onOpenAnnotator && (
            <button
              type="button"
              data-testid={`${kindKey}-open-annotator`}
              onClick={() => onOpenAnnotator(title)}
              className="text-[11px] px-2 py-0.5 border border-[#D35400] text-[#D35400] bg-orange-50 hover:bg-[#D35400] hover:text-white rounded font-semibold transition-colors inline-flex items-center gap-1"
              title={`Open the roof-plan annotator to edit ${title.toLowerCase()} pins. Closes back to here.`}
            ><Pencil size={10} /> Edit on diagram ↗</button>
          )}
          {pendingCount > 0 && onConfirmPins && (
            <button
              type="button"
              data-testid={`${kindKey}-confirm-all`}
              onClick={() => onConfirmPins(null, true)}
              className="text-[11px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold shadow-sm"
              title={`Confirm all ${pendingCount} pending ${title.toLowerCase()} pin${pendingCount === 1 ? '' : 's'} across every option`}
            >✓ Confirm all {pendingCount} pending</button>
          )}
          <div className="text-[10px] font-semibold text-slate-500">
            Mode: <span className="text-slate-800 uppercase">{REPAIR_OPTIONS.find(o => o.key === option)?.title || option}</span>
          </div>
        </div>
      </div>
      <div className="p-3">
        {/* For ALL repair modes we render the FullOptionSummary so the
            Per-pin breakdown stays visible (operator request Feb '26 —
            previously the Replace-ALL toggle collapsed the breakdown
            and hid pin counts). The header summary box + per-pin
            columns adapt to the active mode. */}
        <FullOptionSummary
          cfg={cfg}
          rates={rates}
          option={option}
          caps={caps}
          totalM={totalLm}
          pinKind={pinKind}
          kindKey={kindKey}
          onChange={onChange}
          onJumpToPin={onJumpToPin}
          onConfirmPins={onConfirmPins}
        />
        {/* Rate-source breakdown — surfaces the RidgeTypeDropDown
            row driving this section's quote line. Operator can audit
            every $ that goes into the per-lm rate (Buy / Materials /
            Labour / Contractor) for both Bed & Point and Point modes,
            plus the per-each defect-cap replace rate used during a
            B&P pass. */}
        <RateSourceBreakdown rates={rates} />
      </div>
    </div>
  );
}


/**
 * Compact per-rate-row audit card. Renders 3 columns:
 *   1. Bed & Point (from "Bed & Point Modern" / "Bed & Point Old")
 *   2. Point        (from "Point Modern" / "Point Old")
 *   3. Defect cap   (from B&P row's replace rates)
 * Each column shows Buy / Materials / Labour / Contractor → per-lm
 * total. The per-lm rate the calc actually uses is highlighted (sum
 * of materials + labour, as per the office's standard pricing).
 */
function RateSourceBreakdown({ rates }) {
  const bp = rates?.bed_and_point || {};
  const pt = rates?.point || {};
  const bpRate = (bp.labour_per_lm || 0) + (bp.material_per_lm || 0);
  const ptRate = (pt.labour_per_lm || 0) + (pt.material_per_lm || 0);
  const capEach = (bp.labour_per_cap || 0) + (bp.material_per_cap || 0);
  return (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]" data-testid="rate-source-breakdown">
      <RateBox
        title={`B&P · ${bp.name || '—'}`}
        rows={[
          ['Buy',        bp.buy_per_lm],
          ['Materials',  bp.material_per_lm],
          ['Labour',     bp.labour_per_lm],
          ['Contractor', bp.contractor_per_lm],
        ]}
        totalLabel="per lm (Materials + Labour)"
        totalValue={bpRate}
        tone="rose"
      />
      <RateBox
        title={`Point · ${pt.name || '—'}`}
        rows={[
          ['Buy',        pt.buy_per_lm],
          ['Materials',  pt.material_per_lm],
          ['Labour',     pt.labour_per_lm],
          ['Contractor', pt.contractor_per_lm],
        ]}
        totalLabel="per lm (Materials + Labour)"
        totalValue={ptRate}
        tone="sky"
      />
      <RateBox
        title="Defect cap (B&P · per each)"
        rows={[
          ['Materials replace', bp.material_per_cap],
          ['Labour replace',    bp.labour_per_cap],
        ]}
        totalLabel="per cap"
        totalValue={capEach}
        tone="amber"
      />
    </div>
  );
}

function RateBox({ title, rows, totalLabel, totalValue, tone }) {
  const ring = { rose: 'ring-rose-200 bg-rose-50/40', sky: 'ring-sky-200 bg-sky-50/40', amber: 'ring-amber-200 bg-amber-50/40' }[tone] || 'ring-slate-200 bg-white';
  return (
    <div className={`rounded ring-1 ${ring} p-2`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 truncate" title={title}>{title}</div>
      <table className="w-full mt-1.5">
        <tbody>
          {rows.map(([label, val]) => (
            <tr key={label} className="text-slate-700">
              <td className="py-0.5 pr-1.5">{label}</td>
              <td className="py-0.5 text-right font-mono-num">${Number(val || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-1.5 pt-1.5 border-t border-slate-200 flex items-baseline justify-between">
        <span className="text-[10px] text-slate-500">{totalLabel}</span>
        <strong className="text-sm text-slate-900 font-mono-num">${Number(totalValue || 0).toFixed(2)}</strong>
      </div>
    </div>
  );
}


// Iter126 — per-pin rate detail button. Opens the shared IronRateDetailModal
// via the RateDetailProvider (mounted at the Concrete builder root since
// iter122). Targets the appropriate base rate row (B&P or Point) based on
// the pin's current effective treatment.
function PinRateDetailButton({ treatment, effectiveTreatment, bpRow, ptRow, testid, onSaveOverride }) {
  const ctx = useRateDetail();
  if (!ctx) return null;
  // Build a synthetic rate "row" the modal can hydrate from. Uses the
  // base catalogue rate as the seed; override values overlay it.
  const base = effectiveTreatment === 'bed_and_point' ? bpRow : ptRow;
  if (!base || !base.name) return null;
  const stub = {
    id: `${effectiveTreatment}_${treatment.pin_id || 'pin'}`,
    name: base.name,
    rate: round1((base.labour_per_lm || 0) + (base.material_per_lm || 0)),
    materials_rate: base.material_per_lm || 0,
    labour_rate:    base.labour_per_lm || 0,
    buy_cost: 0,
    contractor_rate: 0,
  };
  const hasOverride = treatment.detail_override && Object.keys(treatment.detail_override).length > 0;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => ctx.open('sheet', stub, {
        label: `${effectiveTreatment === 'bed_and_point' ? 'B&P' : 'Point'} — Pin ${treatment.pin_id || ''}`,
        detailOverride: treatment.detail_override || {},
        onSaveOverride,
      })}
      title="Edit Buy / Material / Labour / Contractor breakdown — applies to this pin only"
      className={`text-[9px] font-bold px-1 py-0 rounded border ${
        hasOverride
          ? 'border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100'
          : 'border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
      }`}>
      {hasOverride ? '✎ Ov' : '✎'}
    </button>
  );
}


function FullOptionSummary({ cfg, rates, option, caps, totalM, pinKind, kindKey, onChange, onJumpToPin, onConfirmPins }) {
  // Auto-calc breakdown toggle (operator request Feb '26): "Show the
  // breakdown of the Auto-calculated Full Bed & Point rate, e.g.
  // breakdown of $68/lm. Have a toggle to show the breakdown or hide
  // it. Same for all calculations." Default OFF so the headline stays
  // clean; clicking the chip below the rate reveals the per-lm
  // contribution from Materials + Labour + Buy + Contractor.
  const [showBreakdown, setShowBreakdown] = useState(false);
  // Default rate driver for "Full B&P" / "Full Repoint" — every pin
  // adopts the option-level treatment by default. The operator can
  // override an individual pin via the breakdown table below to mix
  // treatments inside an otherwise homogeneous scenario (e.g. one
  // ridge needs Bed & Point even though the option is Full Repoint).
  //
  // Override semantics (operator request Feb '26): `treatment_override`
  // is stored ONLY when the user deliberately changes the dropdown.
  // When blank, the pin follows the scenario-level option. This keeps
  // "Full B&P" actually meaning "every pin is B&P" without stale
  // legacy per-pin `treatment` values pulling the total down. The
  // legacy `treatment` field is still respected as a fallback for
  // pre-iter88 data but DOES NOT override the option default — the
  // operator must re-click the dropdown to opt back into a non-default.
  const isReplaceAll = option === 'replace_all';
  const defaultTreatment = option === 'full_bp' ? 'bed_and_point' : 'point';
  const effectiveTreatment = (t) => {
    const ov = (t.treatment_override || '').toLowerCase();
    if (ov === 'bed_and_point' || ov === 'point') return ov;
    // Iter 120 — per-pin Full Cap Replacement opt-in. Reuses the
    // existing `treatment === 'replace'` field the backend already
    // dispatches to `_per_pin_replace_lines` so a checked row bills
    // caps × cap_rate even when the scenario is Part / Full B&P.
    if (ov === 'replace' || (t.treatment || '').toLowerCase() === 'replace') return 'replace';
    return defaultTreatment;
  };
  // Per-pin Full Cap Replacement helper. In Replace-ALL scenario the
  // default action is replace_all (checkbox checked unless the operator
  // ticks `defects_only`). Outside Replace-ALL the checkbox simply
  // promotes the row into the replace-pin path described above.
  const isFullCapReplacement = (t) => {
    if (isReplaceAll) return effectiveAction(t) !== 'defects_only';
    // UAT 2026-02 — checkbox is "checked" when the pin's defect_caps_qty
    // has been mirrored up to its full caps count (see setFullCap).
    const lm = (t.length_override_m != null && t.length_override_m !== '')
      ? parseFloat(t.length_override_m) || 0
      : (parseFloat(t.length_m) || 0);
    const capsCount = Math.max(0, Math.round((lm * 1000) / capLen));
    const dc = parseInt(t.defect_caps_qty || 0, 10);
    return capsCount > 0 && dc >= capsCount;
  };
  // Replace-ALL mode — each pin defaults to "Replace ALL" (the section
  // option), but the operator can flag a pin as "Defects only" so it
  // bills only the per-pin defect_caps_qty instead of a full
  // cap-by-cap replacement. Stored on the treatment as
  // `replace_action_override` ('defects_only' | undefined).
  const effectiveAction = (t) => (t.replace_action_override === 'defects_only' ? 'defects_only' : 'replace_all');
  const cap = caps?.find(c => c.id === cfg.replace_cap_id) || caps?.[0] || null;
  const capLen = parseInt(cfg.replace_cap_len_mm || cap?.len_mm || 420, 10) || 420;
  const capRate = cap ? (cap.material_each + cap.labour_each) : 0;
  const bpSrc = rates?.bed_and_point || {};
  const ptSrc = rates?.point || {};
  const treatments = cfg.treatments || [];
  // Compute per-pin subtotal so a pin overridden to a different
  // treatment is priced correctly. Length override (`length_override_m`)
  // wins over the diagram-measured length. In Replace-ALL mode the
  // per-pin line = caps_count × cap_rate (or defect_caps_qty ×
  // defect_cap_rate when the action is 'defects_only').
  const pinLine = (t) => {
    const lm = (t.length_override_m != null && t.length_override_m !== '')
      ? parseFloat(t.length_override_m) || 0
      : (parseFloat(t.length_m) || 0);
    const capsCount = Math.max(0, Math.round((lm * 1000) / capLen));
    if (isReplaceAll) {
      const act = effectiveAction(t);
      const caps_used = act === 'defects_only' ? (parseInt(t.defect_caps_qty, 10) || 0) : capsCount;
      const defectCapRate = (bpSrc.labour_per_cap || 0) + (bpSrc.material_per_cap || 0);
      const ratePerCap = act === 'defects_only' ? defectCapRate : capRate;
      return { lm, capsCount, action: act, caps_used, ratePerCap, line: caps_used * ratePerCap };
    }
    const tk = effectiveTreatment(t);
    // Iter 120 — pin flagged for Full Cap Replacement (outside
    // Replace-ALL scenario). Mirrors the backend's _per_pin_replace_lines:
    // bill caps_count × cap_rate, not lm × $/m.
    if (tk === 'replace') {
      return { lm, capsCount, ratePerCap: capRate, caps_used: capsCount, line: capsCount * capRate, tk: 'replace' };
    }
    const src = tk === 'bed_and_point' ? bpSrc : ptSrc;
    const ratePerLm = (src.labour_per_lm || 0) + (src.material_per_lm || 0);
    const defectCaps = parseInt(t.defect_caps_qty, 10) || 0;
    const defectCapRate = (bpSrc.labour_per_cap || 0) + (bpSrc.material_per_cap || 0);
    const capLine = option === 'full_bp' ? defectCaps * defectCapRate : 0;
    return { lm, capsCount, ratePerLm, lineLm: lm * ratePerLm, capLine, line: lm * ratePerLm + capLine, tk };
  };
  const linesPerPin = treatments.map(pinLine);
  const overriddenTotalM = round1(linesPerPin.reduce((s, x) => s + x.lm, 0));
  const totalQuoteAll = linesPerPin.reduce((s, x) => s + x.line, 0);
  const totalCapsUsed = linesPerPin.reduce((s, x) => s + (x.caps_used || 0), 0);
  const defectQty = treatments.reduce((s, t) => s + (parseInt(t.defect_caps_qty, 10) || 0), 0);
  const defectRate = (bpSrc.labour_per_cap || 0) + (bpSrc.material_per_cap || 0);
  const headlineRate = (option === 'full_bp' ? bpSrc : ptSrc);
  const headlineRatePerLm = (headlineRate.labour_per_lm || 0) + (headlineRate.material_per_lm || 0);
  const updateTreatment = (idx, patch) => {
    const next = [...treatments];
    // Special-cased: storing `treatment_override === option's default`
    // clears the override (the pin goes back to following the option).
    if ('treatment_override' in patch && (patch.treatment_override === defaultTreatment || patch.treatment_override === '')) {
      const cleaned = { ...next[idx] };
      delete cleaned.treatment_override;
      next[idx] = { ...cleaned, ...patch, treatment_override: undefined };
    } else if ('replace_action_override' in patch && patch.replace_action_override === 'replace_all') {
      // Default = replace_all → clear the override.
      const cleaned = { ...next[idx] };
      delete cleaned.replace_action_override;
      next[idx] = { ...cleaned, ...patch, replace_action_override: undefined };
    } else {
      next[idx] = { ...next[idx], ...patch };
    }
    onChange({ ...cfg, treatments: next });
  };

  /** Iter 120 — toggle the Full Cap Replacement checkbox on a single
   *  pin. Encapsulates the dual semantic across isReplaceAll vs not.
   *
   *  UAT 2026-02 — Outside Replace-ALL scenarios, [Full Cap Replacement]
   *  now MIRRORS the pin's caps count into `defect_caps_qty` so the
   *  billing = (main B&P/Point per-lm line) + (caps × per-each defect
   *  cap rate). Previously the button set `treatment_override='replace'`
   *  which routed the pin to `_per_pin_replace_lines` at the full
   *  replace-cap rate (~$13/cap) — wrong when the operator's intent
   *  is "all caps on this pin are defective, bill them at the defect
   *  cap rate on top of the normal B&P/Point line".
   */
  const setFullCap = (idx, checked) => {
    if (isReplaceAll) {
      updateTreatment(idx, { replace_action_override: checked ? 'replace_all' : 'defects_only' });
      return;
    }
    const t = treatments[idx] || {};
    const lm = (t.length_override_m != null && t.length_override_m !== '')
      ? parseFloat(t.length_override_m) || 0
      : (parseFloat(t.length_m) || 0);
    const capsCount = Math.max(0, Math.round((lm * 1000) / capLen));
    updateTreatment(idx, {
      defect_caps_qty: checked ? capsCount : 0,
      treatment_override: undefined,
    });
  };

  /** Iter 120 — bulk select-all toggle for the new Full Cap
   *  Replacement column header.
   *  UAT 2026-02 — mirror caps → defect_caps_qty per pin (see setFullCap).
   */
  const setFullCapAll = (checked) => {
    const next = treatments.map(t => {
      const copy = { ...t };
      if (isReplaceAll) {
        if (checked) delete copy.replace_action_override;
        else copy.replace_action_override = 'defects_only';
        return copy;
      }
      const lm = (copy.length_override_m != null && copy.length_override_m !== '')
        ? parseFloat(copy.length_override_m) || 0
        : (parseFloat(copy.length_m) || 0);
      const capsCount = Math.max(0, Math.round((lm * 1000) / capLen));
      copy.defect_caps_qty = checked ? capsCount : 0;
      if ((copy.treatment_override || '').toLowerCase() === 'replace') delete copy.treatment_override;
      return copy;
    });
    onChange({ ...cfg, treatments: next });
  };

  const allChecked = treatments.length > 0 && treatments.every(isFullCapReplacement);
  const someChecked = treatments.some(isFullCapReplacement);
  return (
    <div className="space-y-3" data-testid={`${kindKey}-full-summary`}>
      {isReplaceAll ? (
        <div className="p-3 rounded bg-teal-50 border border-teal-200">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider opacity-80 text-teal-900">
                Replace ALL · auto-calculated · {cap?.name || '— pick cap below —'}
              </div>
              <div className="text-2xl font-bold font-mono-num mt-0.5 text-teal-900">
                {totalCapsUsed} cap{totalCapsUsed === 1 ? '' : 's'} × ${capRate.toFixed(2)}/ea
              </div>
              {/* Cap-rate breakdown toggle — operator request Feb '26. */}
              <button
                type="button"
                data-testid={`${kindKey}-rate-breakdown-toggle`}
                onClick={() => setShowBreakdown(v => !v)}
                className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-teal-900/75 hover:text-teal-900 underline-offset-2 hover:underline"
              >
                {showBreakdown ? '▾ Hide rate breakdown' : '▸ Show rate breakdown'}
              </button>
              {showBreakdown && (
                <div className="mt-1.5 text-[11px] grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono-num text-teal-900" data-testid={`${kindKey}-rate-breakdown-detail`}>
                  <span>Materials</span><span className="text-right">${(cap?.material_each || 0).toFixed(2)}/ea</span>
                  <span>Labour</span><span className="text-right">${(cap?.labour_each || 0).toFixed(2)}/ea</span>
                  <span className="col-span-2 border-t border-teal-300 pt-0.5">
                    <span className="inline-flex justify-between w-full"><span>= per-cap rate</span><strong>${capRate.toFixed(2)}/ea</strong></span>
                  </span>
                  <span className="text-[10px] opacity-75">Cap length</span><span className="text-right text-[10px] opacity-75">{capLen}mm</span>
                  <span className="text-[10px] opacity-75">Coverage</span><span className="text-right text-[10px] opacity-75">{overriddenTotalM.toFixed(2)}m ÷ {capLen}mm = {totalCapsUsed} caps</span>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs opacity-70">Line total</div>
              <div className="text-2xl font-bold font-mono-num text-teal-900">${totalQuoteAll.toFixed(2)}</div>
              {showBreakdown && (
                <div className="mt-1.5 text-[10px] text-teal-900/75 font-mono-num">
                  {totalCapsUsed} × ${capRate.toFixed(2)} = <strong>${(totalCapsUsed * capRate).toFixed(2)}</strong>
                </div>
              )}
            </div>
          </div>
          <div className="text-[11px] text-teal-800/80 italic mt-1">Total caps = total {pinKind.toLowerCase()} length ÷ {capLen}mm cap length. Per-pin Action column below lets you mark individual pins as "Defects only" to skip full replacement on that pin and bill only the recorded defect caps.</div>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <label className="text-[11px] font-semibold text-teal-900">Cap type</label>
            <select
              data-testid={`${kindKey}-replace-cap-select`}
              value={cfg.replace_cap_id || ''}
              onChange={e => {
                const c = caps.find(cp => cp.id === e.target.value);
                onChange({ ...cfg, replace_cap_id: e.target.value, replace_cap_len_mm: c?.len_mm || cfg.replace_cap_len_mm });
              }}
              className="px-2 py-1 border border-teal-300 rounded text-xs bg-white min-w-[16rem]"
            >
              {caps.map(c => <option key={c.id} value={c.id}>{c.name} — {c.len_mm}mm · ${(c.material_each + c.labour_each).toFixed(2)} ea</option>)}
            </select>
            {cfg.replace_cap_id && (
              <>
                <RateDetailEditButton
                  component={kindKey === 'barge' ? 'barge' : 'ridge'}
                  rate={caps.find(c => c.id === cfg.replace_cap_id)}
                  testid={`${kindKey}-replace-cap-detail`}
                />
                <EditRateLink component={kindKey} rateId={cfg.replace_cap_id} testid={`${kindKey}-replace-cap-edit`} />
              </>
            )}
          </div>
        </div>
      ) : (
      <div className={`p-3 rounded ${option === 'full_bp' ? 'bg-rose-50 border border-rose-200' : 'bg-sky-50 border border-sky-200'}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider opacity-80">
              {(() => {
                // iter313h — Barge-specific titles. Trevor's UAT feedback:
                // "when all the barges are Bed & Point I am not building a
                // Full Repoint — it should be a Full Bed & Point Barges."
                const isBarge = kindKey === 'barge';
                if (option === 'full_bp') return isBarge ? 'Full Bed & Point Barges' : 'Full Bed & Point';
                return isBarge ? 'Full Repoint Barges' : 'Full Repoint';
              })()} · auto-calculated
            </div>
            <div className="text-2xl font-bold font-mono-num mt-0.5">
              {overriddenTotalM.toFixed(1)}m × ${headlineRatePerLm.toFixed(2)}/lm
            </div>
            {/* Per-lm rate breakdown toggle — operator request Feb '26. */}
            <button
              type="button"
              data-testid={`${kindKey}-rate-breakdown-toggle`}
              onClick={() => setShowBreakdown(v => !v)}
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-75 hover:opacity-100 underline-offset-2 hover:underline"
            >
              {showBreakdown ? '▾ Hide rate breakdown' : '▸ Show rate breakdown'}
            </button>
            {showBreakdown && (() => {
              const src = option === 'full_bp' ? bpSrc : ptSrc;
              const mat = src.material_per_lm || 0;
              const lab = src.labour_per_lm || 0;
              const buy = src.buy_per_lm || 0;
              const con = src.contractor_per_lm || 0;
              return (
                <div className="mt-1.5 text-[11px] grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono-num" data-testid={`${kindKey}-rate-breakdown-detail`}>
                  <span className="text-slate-700">Materials</span><span className="text-right">${mat.toFixed(2)}/lm</span>
                  <span className="text-slate-700">Labour</span><span className="text-right">${lab.toFixed(2)}/lm</span>
                  <span className="text-slate-500 col-span-2 border-t border-slate-300 pt-0.5">
                    <span className="inline-flex justify-between w-full"><span>= per-lm rate</span><strong>${headlineRatePerLm.toFixed(2)}/lm</strong></span>
                  </span>
                  <span className="text-slate-500 text-[10px]">Buy cost (ref)</span><span className="text-right text-slate-500 text-[10px]">${buy.toFixed(2)}/lm</span>
                  <span className="text-slate-500 text-[10px]">Contractor (ref)</span><span className="text-right text-slate-500 text-[10px]">${con.toFixed(2)}/lm</span>
                </div>
              );
            })()}
          </div>
          <div className="text-right">
            <div className="text-xs opacity-70">Line total</div>
            <div className="text-2xl font-bold font-mono-num">${totalQuoteAll.toFixed(2)}</div>
            {showBreakdown && (
              <div className="mt-1.5 text-[10px] opacity-75 font-mono-num">
                {overriddenTotalM.toFixed(1)}m × ${headlineRatePerLm.toFixed(2)} = <strong>${(overriddenTotalM * headlineRatePerLm).toFixed(2)}</strong>
              </div>
            )}
          </div>
        </div>
        <div className="text-[11px] opacity-70 italic mt-1">Total quantity pulled from the {treatments.length} {pinKind.toLowerCase()} pin{treatments.length === 1 ? '' : 's'} on the master diagram. Override individual pins below if a ridge needs a different treatment, length, or defect-cap count.</div>
      </div>
      )}

      {/* Per-pin breakdown — always open across every mode (operator
          request Feb '26). Provides per-pin override of treatment /
          length / defect caps / replace action, with a live per-pin
          subtotal so the operator can audit and adjust without
          leaving whichever option flow they're in. */}
      <details className="bg-white border border-slate-200 rounded" open data-testid={`${kindKey}-pin-breakdown`}>
        <summary className="cursor-pointer text-xs font-semibold text-slate-700 px-3 py-2 hover:bg-slate-50 flex items-center justify-between">
          <span><MapPin size={11} className="inline mr-1" /> Per-pin breakdown ({treatments.length} {pinKind.toLowerCase()}{treatments.length === 1 ? '' : 's'} · {isReplaceAll ? totalCapsUsed + ' cap' + (totalCapsUsed === 1 ? '' : 's') : defectQty + ' defect cap' + (defectQty === 1 ? '' : 's') + (option === 'full_bp' ? ' @ $' + defectRate.toFixed(2) + ' ea' : '')})</span>
          <span className="text-[10px] text-slate-500 font-mono-num">${totalQuoteAll.toFixed(2)} ▾</span>
        </summary>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-1.5 px-2 w-28">Pin</th>
                <th className="text-right px-2">Measured</th>
                <th className="text-right px-2">Override m</th>
                <th className="text-right px-2">Caps</th>
                {/* Iter 120 — Full Cap Replacement checkbox column with bulk select-all. */}
                <th className="text-center px-2 w-32">
                  <label className="inline-flex items-center justify-center gap-1 cursor-pointer normal-case font-bold text-slate-600" title="Tick to flag every pin for Full Cap Replacement. Untick to clear all.">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                      onChange={(e) => setFullCapAll(e.target.checked)}
                      className="w-3.5 h-3.5 accent-teal-600"
                      data-testid={`${kindKey}-full-cap-replacement-toggle-all`}
                    />
                    <span className="text-[10px]">Full Cap Replacement</span>
                  </label>
                </th>
                {isReplaceAll
                  ? <th className="text-left px-2">Action</th>
                  : <th className="text-left px-2">Repair Type</th>}
                <th className="text-right px-2">Rate</th>
                <th className="text-right px-2">Defect caps</th>
                <th className="text-right px-2">Line $</th>
              </tr>
            </thead>
            <tbody>
              {treatments.length === 0 && (
                <tr><td colSpan={9} className="text-center py-3 text-slate-400 italic">No {pinKind.toLowerCase()} pins on the master diagram.</td></tr>
              )}
              {treatments.map((t, i) => {
                const L = linesPerPin[i] || {};
                const isOver = (t.length_override_m != null && t.length_override_m !== '');
                const act = effectiveAction(t);
                return (
                  <tr key={t.pin_id || i} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 ${!t.confirmed ? 'bg-amber-50/40' : ''} ${isReplaceAll && act === 'defects_only' ? 'bg-amber-50/40' : ''}`} data-pending={!t.confirmed ? '1' : '0'}>
                    <td className="py-1.5 px-2">
                      <div className="flex items-center gap-1">
                        <JumpToPinButton pinId={t.pin_id || `${pinKind[0]}${i + 1}`} pinType={pinKind} lengthM={L.lm} onJumpToPin={onJumpToPin} />
                        <ConfirmChip rawId={t.raw_id} confirmed={!!t.confirmed} onConfirmPins={onConfirmPins} testidPrefix={kindKey} testidIdx={i} />
                      </div>
                    </td>
                    <td className="text-right px-2 font-mono-num text-slate-500">{(t.length_m || 0).toFixed(2)}</td>
                    <td className="text-right px-2">
                      <input
                        type="number" step="0.1" min="0"
                        data-testid={`${kindKey}-length-override-${i}`}
                        value={t.length_override_m == null ? '' : t.length_override_m}
                        onChange={e => updateTreatment(i, { length_override_m: e.target.value === '' ? null : parseFloat(e.target.value) })}
                        placeholder={`${(t.length_m || 0).toFixed(2)}`}
                        title="Manual length override (leave blank to use the measured length)"
                        className={`w-16 px-1.5 py-1 border rounded text-xs text-right font-mono-num ${isOver ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`}
                      />
                    </td>
                    {/* Caps column — N caps required to span this pin at the chosen cap length. */}
                    <td className="text-right px-2 font-mono-num text-slate-700" data-testid={`${kindKey}-caps-count-${i}`}>{L.capsCount || 0}</td>
                    {/* Iter 120 — Full Cap Replacement checkbox per pin. */}
                    <td className="text-center px-2">
                      <input
                        type="checkbox"
                        checked={isFullCapReplacement(t)}
                        onChange={(e) => setFullCap(i, e.target.checked)}
                        data-testid={`${kindKey}-full-cap-replacement-${i}`}
                        title={isReplaceAll
                          ? (isFullCapReplacement(t) ? 'Replace every cap on this pin — untick to bill defects only.' : 'Defects-only — this pin will only bill the recorded defect_caps_qty.')
                          : (isFullCapReplacement(t) ? 'Pin flagged for Full Cap Replacement — bills caps × cap rate.' : 'Tick to bill this pin as Full Cap Replacement (caps × cap rate).')}
                        className="w-4 h-4 accent-teal-600 cursor-pointer"
                      />
                    </td>
                    {isReplaceAll ? (
                      <td className="px-2">
                        <select
                          data-testid={`${kindKey}-replace-action-${i}`}
                          value={act}
                          onChange={e => updateTreatment(i, { replace_action_override: e.target.value })}
                          title={act === 'defects_only' ? 'Bills only the recorded defect_caps_qty on this pin' : 'Replaces every cap on this pin'}
                          className={`px-1.5 py-1 border rounded text-xs bg-white ${act === 'defects_only' ? 'border-amber-400 bg-amber-50 font-semibold' : 'border-slate-200'}`}
                        >
                          <option value="replace_all">Replace ALL caps</option>
                          <option value="defects_only">Defects only</option>
                        </select>
                      </td>
                    ) : (
                      <td className="px-2">
                        {effectiveTreatment(t) === 'replace' ? (
                          <span className="px-2 py-1 text-xs italic text-teal-700"
                                data-testid={`${kindKey}-repair-type-disabled-${i}`}>
                            Full Cap Replacement
                          </span>
                        ) : (
                          <select
                            data-testid={`${kindKey}-treatment-${i}`}
                            value={effectiveTreatment(t)}
                            onChange={e => updateTreatment(i, { treatment_override: e.target.value })}
                            title={t.treatment_override ? 'Overridden — pick the option default to clear' : `Following the scenario default (${defaultTreatment === 'bed_and_point' ? 'Bed & Point' : 'Point'})`}
                            className={`px-1.5 py-1 border rounded text-xs bg-white ${t.treatment_override ? 'border-amber-400 bg-amber-50 font-semibold' : 'border-slate-200'}`}
                          >
                            <option value="bed_and_point">Bed & Point</option>
                            <option value="point">Point</option>
                          </select>
                        )}
                      </td>
                    )}
                    <td className="text-right px-2 font-mono-num text-slate-600">
                      {isReplaceAll
                        ? <>${(L.ratePerCap || 0).toFixed(2)}/ea</>
                        : (effectiveTreatment(t) === 'replace'
                            ? <>${(L.ratePerCap || 0).toFixed(2)}/ea</>
                            : (
                              <span className="inline-flex items-center gap-1">
                                <span className={(t.detail_override && Object.keys(t.detail_override).length > 0) ? 'text-amber-700 font-semibold' : ''}>
                                  ${((t.detail_override && (
                                    (Number(t.detail_override.materials_rate || 0)) +
                                    (Number(t.detail_override.labour_rate || 0))
                                  )) || (L.ratePerLm || 0)).toFixed(2)}/lm
                                </span>
                                <PinRateDetailButton
                                  treatment={t}
                                  effectiveTreatment={effectiveTreatment(t)}
                                  bpRow={bpSrc}
                                  ptRow={ptSrc}
                                  testid={`${kindKey}-pin-rate-detail-${i}`}
                                  onSaveOverride={(body) => updateTreatment(i, { detail_override: body })}
                                />
                              </span>
                            )
                          )}
                    </td>
                    <td className="text-right px-2">
                      <input
                        type="number" step="1" min="0"
                        data-testid={`${kindKey}-defect-${i}`}
                        value={t.defect_caps_qty || 0}
                        onChange={e => updateTreatment(i, { defect_caps_qty: parseInt(e.target.value, 10) || 0 })}
                        className="w-12 px-1.5 py-1 border border-slate-200 rounded text-xs text-right font-mono-num"
                      />
                    </td>
                    <td className="text-right px-2 font-mono-num font-semibold text-slate-900">${(L.line || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            {treatments.length > 0 && (
              <tfoot className="text-xs font-bold text-slate-700 bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td className="px-2 py-1.5">Total</td>
                  <td className="text-right px-2 font-mono-num">{round1(treatments.reduce((s, t) => s + (parseFloat(t.length_m) || 0), 0)).toFixed(2)}m</td>
                  <td className="text-right px-2 font-mono-num">{overriddenTotalM.toFixed(2)}m</td>
                  <td className="text-right px-2 font-mono-num">{linesPerPin.reduce((s, x) => s + (x.capsCount || 0), 0)}</td>
                  {/* Iter 120 — Full Cap Replacement count in the totals row. */}
                  <td className="text-center px-2 font-mono-num" data-testid={`${kindKey}-full-cap-replacement-count`}>
                    {treatments.filter(isFullCapReplacement).length} / {treatments.length}
                  </td>
                  <td className="px-2 text-[10px] text-slate-500 font-normal italic">{isReplaceAll ? 'amber rows = defects only' : 'override wins over measured'}</td>
                  <td></td>
                  <td className="text-right px-2 font-mono-num">{defectQty}</td>
                  <td className="text-right px-2 font-mono-num text-base">${totalQuoteAll.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </details>
    </div>
  );
}

/** 1-decimal rounding helper used across the FullOptionSummary
 *  recompute path. Module-local so multiple sections can share it. */
function round1(n) { return Math.round((Number(n) || 0) * 10) / 10; }


function ReplaceAllControls({ cfg, caps, totalM, pinKind, kindKey, onChange }) {
  return (
    <div className="space-y-2">
      <div className="bg-teal-50 border border-teal-200 rounded p-3 text-xs text-teal-900">
        <strong>Replace ALL</strong> — total {pinKind.toLowerCase()} length ÷ cap length = N new caps. Defect counts on individual pins are ignored in this mode.
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs font-semibold text-slate-600">Cap type</label>
        <select
          data-testid={`${kindKey}-replace-cap-select`}
          value={cfg.replace_cap_id || ''}
          onChange={e => {
            const cap = caps.find(c => c.id === e.target.value);
            onChange({ ...cfg, replace_cap_id: e.target.value, replace_cap_len_mm: cap?.len_mm || cfg.replace_cap_len_mm });
          }}
          className="px-2 py-1.5 border border-slate-200 rounded text-xs bg-white min-w-[14rem]"
        >
          {caps.map(c => <option key={c.id} value={c.id}>{c.name} — {c.len_mm}mm · ${(c.material_each + c.labour_each).toFixed(2)} ea</option>)}
        </select>
        {cfg.replace_cap_id && (
          <>
            <RateDetailEditButton
              component={kindKey === 'barge' ? 'barge' : 'ridge'}
              rate={caps.find(c => c.id === cfg.replace_cap_id)}
              testid={`${kindKey}-replace-all-cap-detail`}
            />
            <EditRateLink component={kindKey} rateId={cfg.replace_cap_id} testid={`${kindKey}-replace-all-cap-edit`} />
          </>
        )}
        <div className="text-xs text-slate-600">
          Total caps ≈ <strong>{Math.round((totalM * 1000) / (cfg.replace_cap_len_mm || 420))}</strong> ({totalM.toFixed(1)}m ÷ {cfg.replace_cap_len_mm || 420}mm)
        </div>
      </div>
    </div>
  );
}


function PerPinTable({ treatments, updateTreatment, pinKind, kindKey, onJumpToPin, onConfirmPins }) {
  return (
    <table className="w-full text-xs" data-testid={`${kindKey}-treatments-table`}>
      <thead className="text-slate-500 border-b border-slate-200">
        <tr>
          <th className="text-left py-1.5 w-32">Pin</th>
          <th className="text-right">Length</th>
          <th className="text-left pl-3">Treatment</th>
          <th className="text-left pl-3">Split</th>
          <th className="text-right">Opposite m</th>
          <th className="text-right">Defect caps</th>
        </tr>
      </thead>
      <tbody>
        {treatments.length === 0 && (
          <tr><td colSpan={6} className="text-center py-3 text-slate-400 italic">No {pinKind.toLowerCase()} pins on the master diagram.</td></tr>
        )}
        {treatments.map((t, i) => (
          <tr key={t.pin_id || i} className={`border-b border-slate-100 last:border-0 ${!t.confirmed ? 'bg-amber-50/40' : ''}`} data-pending={!t.confirmed ? '1' : '0'}>
            <td className="py-1.5">
              <div className="flex items-center gap-1">
                <JumpToPinButton pinId={t.pin_id || `${pinKind[0]}${i + 1}`} pinType={pinKind} lengthM={t.length_m} onJumpToPin={onJumpToPin} />
                <ConfirmChip rawId={t.raw_id} confirmed={!!t.confirmed} onConfirmPins={onConfirmPins} testidPrefix={`${kindKey}-summary`} testidIdx={i} />
              </div>
            </td>
            <td className="text-right font-mono-num">{(t.length_m || 0).toFixed(1)}m</td>
            <td className="pl-3">
              <select
                data-testid={`${kindKey}-treatment-${i}`}
                value={t.treatment || 'point'}
                onChange={e => updateTreatment(i, { treatment: e.target.value })}
                className={`px-1.5 py-1 border rounded text-xs bg-white ${(t.treatment || '').toLowerCase() === 'replace' ? 'border-teal-400 ring-1 ring-teal-200' : 'border-slate-200'}`}
              >
                <option value="bed_and_point">Bed &amp; Point</option>
                <option value="point">Point</option>
                <option value="mixture">Mixture (50/50)</option>
                <option value="replace">Replace ← uses Default Ridge Cap</option>
              </select>
            </td>
            <td className="pl-3">
              <label className="inline-flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  data-testid={`${kindKey}-split-${i}`}
                  checked={!!t.split}
                  onChange={e => updateTreatment(i, { split: e.target.checked, split_length_m: e.target.checked ? (t.split_length_m || 0) : 0 })}
                  className="rounded"
                />
                <span className="text-slate-500">Split</span>
              </label>
            </td>
            <td className="text-right">
              {t.split ? (
                <input
                  type="number" step="0.1" min="0" max={t.length_m}
                  data-testid={`${kindKey}-split-length-${i}`}
                  value={t.split_length_m || 0}
                  onChange={e => updateTreatment(i, { split_length_m: parseFloat(e.target.value) || 0 })}
                  className="w-16 px-1.5 py-1 border border-slate-200 rounded text-xs text-right font-mono-num"
                />
              ) : <span className="text-slate-300">—</span>}
            </td>
            <td className="text-right">
              <input
                type="number" step="1" min="0"
                data-testid={`${kindKey}-defect-${i}`}
                value={t.defect_caps_qty || 0}
                onChange={e => updateTreatment(i, { defect_caps_qty: parseInt(e.target.value, 10) || 0 })}
                className="w-14 px-1.5 py-1 border border-slate-200 rounded text-xs text-right font-mono-num"
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


// ── Tile defect section ───────────────────────────────────────────────

function TileSection({ cfg, roofDetails, tileRate, appraisalQty, onChange }) {
  if (!cfg) return null;
  // ── Quantity precedence (must match `_tile_defect_lines` on backend) ──
  // defect_buffer_qty (preferred) → appraisal + buffer
  //   > job_defect_total_override (legacy absolute)
  //   > defect_qty_override (legacy absolute)
  //   > appraisalQty (counted on the original roof appraisal)
  const apprQty = parseInt(appraisalQty, 10) || 0;
  let effectiveQty;
  if (cfg.defect_buffer_qty != null) {
    effectiveQty = Math.max(0, apprQty + parseInt(cfg.defect_buffer_qty, 10));
  } else if (cfg.job_defect_total_override != null) {
    effectiveQty = parseInt(cfg.job_defect_total_override, 10) || 0;
  } else if (cfg.defect_qty_override != null) {
    effectiveQty = parseInt(cfg.defect_qty_override, 10) || 0;
  } else {
    effectiveQty = apprQty;
  }
  const buffer = cfg.defect_buffer_qty != null
    ? parseInt(cfg.defect_buffer_qty, 10)
    : (effectiveQty - apprQty);
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="tile-section">
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
          <Layers size={14} /> Tile defects
        </div>
        {/* Brand · Profile pulled from Roof Details (auto-linked at
            the appraisal level — see the Stage 1 header dropdowns
            above). Displayed here as labels so the operator can
            confirm the tile being repaired matches the existing roof
            without leaving this section. */}
        <div className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
          <span>Existing roof:</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded">
            <span className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Brand</span>
            <span className="font-semibold text-slate-900" data-testid="tile-brand-label">{roofDetails.tile_brand || '—'}</span>
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded">
            <span className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Profile</span>
            <span className="font-semibold text-slate-900" data-testid="tile-profile-label">{roofDetails.tile_profile || '—'}</span>
          </span>
          {roofDetails.tile_type && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded">
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-bold">Type</span>
              <span className="font-semibold text-slate-900">{roofDetails.tile_type}</span>
            </span>
          )}
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_2fr] gap-3 items-center">
          {/* Read-only chip showing the count noted on the appraisal */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Defect qty (from appraisal)</label>
            <div className="text-lg font-mono-num font-bold text-slate-900" data-testid="tile-defect-appraisal-qty">{apprQty}</div>
          </div>
          {/* Buffer dropdown — preset units of 5, picked by the
              operator to allow for additional defects discovered
              during the repair pass. The "Job Defect Total" below
              is computed live (apprQty + buffer). */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Buffer (extras allowed)</label>
            <select
              data-testid="tile-defect-buffer"
              value={cfg.defect_buffer_qty == null ? '' : String(cfg.defect_buffer_qty)}
              onChange={e => {
                const v = e.target.value;
                onChange({
                  ...cfg,
                  defect_buffer_qty: v === '' ? null : parseInt(v, 10),
                  // Clear the legacy absolute overrides so the buffer
                  // arithmetic is the single source of truth from now on.
                  job_defect_total_override: null,
                  defect_qty_override: null,
                });
              }}
              className="mt-0.5 w-full px-2 py-1 border border-slate-300 rounded text-sm bg-white font-mono-num"
            >
              <option value="">— None —</option>
              {[0, 5, 10, 15, 20, 25, 30, 40, 50, 75, 100].map(n => (
                <option key={n} value={n}>+{n}</option>
              ))}
            </select>
          </div>
          {/* iter313h — Manual override input (Trevor UAT feedback):
              lets operator type an absolute qty without going back to
              the roof-appraisal to bump `tile_defect_qty`. Setting
              this clears the buffer so the precedence chain stays
              honest — defect_qty_override wins over appraisal_qty. */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Manual override (tiles)</label>
            <input
              data-testid="tile-defect-manual-override"
              type="number"
              min="0"
              step="1"
              value={cfg.defect_qty_override == null ? '' : String(cfg.defect_qty_override)}
              onChange={e => {
                const raw = e.target.value;
                const parsed = raw === '' ? null : parseInt(raw, 10);
                onChange({
                  ...cfg,
                  defect_qty_override: (Number.isNaN(parsed) ? null : parsed),
                  // Clearing the buffer + legacy job_defect_total
                  // makes defect_qty_override the winning value in
                  // the backend precedence chain (see _tile_defect_lines).
                  defect_buffer_qty: null,
                  job_defect_total_override: null,
                });
              }}
              placeholder={String(apprQty)}
              className="mt-0.5 w-full px-2 py-1 border border-slate-300 rounded text-sm bg-white font-mono-num"
              title="Type an exact tile qty. Overrides the appraisal count + buffer. Leave blank to fall back to buffer / appraisal count."
            />
          </div>
          {/* Computed Job Defect Total */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
              Job Defect Total
            </label>
            <div className="flex items-center gap-1.5">
              <div className="text-lg font-mono-num font-bold text-slate-900" data-testid="tile-job-defect-total">{effectiveQty}</div>
              {(cfg.defect_buffer_qty != null || cfg.job_defect_total_override != null || cfg.defect_qty_override != null) && (
                <button
                  data-testid="tile-job-defect-revert"
                  onClick={() => onChange({ ...cfg, defect_buffer_qty: null, job_defect_total_override: null, defect_qty_override: null })}
                  className="text-[10px] text-[#D35400] underline"
                  title={`Reset back to the appraisal defect count (${apprQty})`}
                >reset</button>
              )}
            </div>
            <div className="text-[10px] text-slate-500 font-mono-num">
              {apprQty} {buffer >= 0 ? '+' : '−'} {Math.abs(buffer)} = <strong className="text-slate-700">{effectiveQty}</strong>
            </div>
          </div>
          {/* Rate × qty preview */}
          <div className="text-xs text-slate-600">
            <div>Rate: <strong className="font-mono-num">${tileRate.rate_each.toFixed(2)}</strong> ea <span className="text-slate-400">({tileRate.name})</span></div>
            <div className="mt-0.5">Line total: <strong className="text-base font-mono-num text-slate-900">${(effectiveQty * tileRate.rate_each).toFixed(2)}</strong></div>
          </div>
        </div>
        {apprQty === 0 && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            No tile defects noted on the original appraisal. If the operator estimates defects will be discovered during the repair, set <strong>Job Defect Total</strong> above.
          </div>
        )}
      </div>
    </div>
  );
}


// ── Valleys section ───────────────────────────────────────────────────

function ValleySection({ cfg, options, pendingCount = 0, onChange, onJumpToPin, onOpenAnnotator, onConfirmPins }) {
  if (!cfg) return null;
  const replacements = cfg.replacements || [];
  const update = (idx, patch) => {
    const next = [...replacements];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...cfg, replacements: next });
  };
  // Section-level summary so the operator sees pin count + total
  // length at a glance before drilling into individual pin rows.
  // Pins marked "Leave" don't contribute to the Replace m² / $$$
  // totals (consistent with the backend `_valley_lines` skip).
  const totalLengthAll = replacements.reduce((s, v) => s + (parseFloat(v.length_m) || 0), 0);
  const isReplace = (v) => ((v.action || 'replace').toLowerCase() !== 'leave');
  const totalReplaceAll = replacements.reduce((s, v) => s + (isReplace(v) ? (parseFloat(v.replace_length_m) || 0) : 0), 0);
  const totalLineAll = replacements.reduce((s, v) => {
    if (!isReplace(v)) return s;
    const opt = options.find(o => o.id === v.valley_option_id);
    return s + ((parseFloat(v.replace_length_m) || 0) * (opt?.rate_per_m || 0));
  }, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="valley-section">
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
          <Triangle size={14} className="rotate-180" /> Valleys
          {pendingCount > 0 && (
            <span data-testid="valley-pending-badge" className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded ring-1 ring-amber-300">⚠ {pendingCount} pending</span>
          )}
        </div>
        {/* Header summary chips — total qty + total length so the
            operator gets the section's headline at a glance before
            reading per-pin rows. */}
        <div className="flex items-center gap-1.5 text-[11px] flex-wrap" data-testid="valley-section-summary">
          {onOpenAnnotator && (
            <button
              type="button"
              data-testid="valley-open-annotator"
              onClick={() => onOpenAnnotator('Valleys')}
              className="text-[11px] px-2 py-0.5 border border-[#D35400] text-[#D35400] bg-orange-50 hover:bg-[#D35400] hover:text-white rounded font-semibold transition-colors inline-flex items-center gap-1"
              title="Open the roof-plan annotator to edit Valley pins. Closes back to here."
            ><Pencil size={10} /> Edit on diagram ↗</button>
          )}
          {pendingCount > 0 && onConfirmPins && (
            <button
              type="button"
              data-testid="valley-confirm-all"
              onClick={() => onConfirmPins(null, true)}
              className="text-[11px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold shadow-sm"
              title={`Confirm all ${pendingCount} pending Valley pin${pendingCount === 1 ? '' : 's'}`}
            >✓ Confirm all {pendingCount} pending</button>
          )}
          <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded font-semibold text-slate-700">Qty: <strong className="font-mono-num">{replacements.length}</strong></span>
          <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded font-semibold text-slate-700">Total length: <strong className="font-mono-num">{totalLengthAll.toFixed(1)}m</strong></span>
          <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded font-semibold text-emerald-800">Replace: <strong className="font-mono-num">{totalReplaceAll.toFixed(1)}m</strong></span>
          <span className="px-2 py-0.5 bg-indigo-50 border border-indigo-200 rounded font-semibold text-indigo-800">$<strong className="font-mono-num">{totalLineAll.toFixed(2)}</strong></span>
        </div>
      </div>
      <div className="p-3">
        {replacements.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No Valley pins on the master diagram.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-1.5 w-12">Qty</th>
                <th className="text-left w-32">Pin</th>
                <th className="text-right">Length per pin</th>
                <th className="text-right">Total length</th>
                <th className="text-left">Action</th>
                <th className="text-right">Replace length</th>
                <th className="text-left pl-3">New material / profile</th>
                <th className="text-right">Line total</th>
              </tr>
            </thead>
            <tbody>
              {replacements.map((v, i) => {
                const opt = options.find(o => o.id === v.valley_option_id);
                const action = (v.action || 'replace').toLowerCase();
                const isLeave = action === 'leave';
                const lineTotal = isLeave ? 0 : (v.replace_length_m || 0) * (opt?.rate_per_m || 0);
                const lenPerPin = parseFloat(v.length_m) || 0;
                // Per-pin qty — at the moment one row = one pin, but
                // surfacing the qty column makes the section's
                // counting model explicit (mirrors how the Aerial
                // captures pin clusters when reading the diagram).
                const qty = 1;
                return (
                  <tr key={v.pin_id || i} className={`border-b border-slate-100 last:border-0 ${isLeave ? 'bg-slate-50 text-slate-400' : ''}`}>
                    <td className="py-1.5 font-mono-num">{qty}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <JumpToPinButton pinId={v.pin_id || `V${i + 1}`} pinType="Valley" lengthM={v.length_m} onJumpToPin={onJumpToPin} />
                        <ConfirmChip rawId={v.raw_id} confirmed={!!v.confirmed} onConfirmPins={onConfirmPins} testidPrefix="valley" testidIdx={i} />
                      </div>
                    </td>
                    <td className="text-right font-mono-num">{lenPerPin.toFixed(2)}m</td>
                    <td className="text-right font-mono-num">{(qty * lenPerPin).toFixed(2)}m</td>
                    <td>
                      <select
                        data-testid={`valley-action-${i}`}
                        value={action}
                        onChange={e => {
                          // UAT 2026-02 — switching to Replace preloads
                          // `replace_length_m` from the pin's measured
                          // length when the operator hasn't set one yet.
                          const nextAction = e.target.value;
                          const patch = { action: nextAction };
                          if (nextAction === 'replace' && !(parseFloat(v.replace_length_m) || 0)) {
                            patch.replace_length_m = parseFloat(v.length_m) || 0;
                          }
                          update(i, patch);
                        }}
                        onDoubleClick={() => {
                          const nextAction = action === 'leave' ? 'replace' : 'leave';
                          const patch = { action: nextAction };
                          if (nextAction === 'replace' && !(parseFloat(v.replace_length_m) || 0)) {
                            patch.replace_length_m = parseFloat(v.length_m) || 0;
                          }
                          update(i, patch);
                        }}
                        title={(isLeave ? 'This valley will NOT appear on the quote' : 'Replace — include in the quote with the material below') + ' · Double-click to cycle'}
                        className={`px-1.5 py-1 border rounded text-xs bg-white ${isLeave ? 'border-slate-300 text-slate-600 font-semibold' : 'border-emerald-400 text-emerald-800 font-semibold'}`}
                      >
                        <option value="replace">Replace</option>
                        <option value="leave">Leave</option>
                      </select>
                    </td>
                    <td className="text-right">
                      <input
                        type="number" step="0.5" min="0" max={v.length_m}
                        data-testid={`valley-replace-length-${i}`}
                        value={v.replace_length_m || 0}
                        onChange={e => update(i, { replace_length_m: parseFloat(e.target.value) || 0 })}
                        disabled={isLeave}
                        className={`w-20 px-1.5 py-1 border border-slate-200 rounded text-xs text-right font-mono-num ${isLeave ? 'bg-slate-100' : ''}`}
                      />
                    </td>
                    <td className="pl-3">
                      <div className="flex items-center gap-1.5">
                        <select
                          data-testid={`valley-option-${i}`}
                          value={v.valley_option_id || ''}
                          onChange={e => {
                            // UAT 2026-02 — picking a valley material
                            // also preloads replace_length_m from the
                            // pin length when unset (guards Note 1.5
                            // where Maxx totalled $0 on default 0-length).
                            const patch = { valley_option_id: e.target.value };
                            if (e.target.value && !(parseFloat(v.replace_length_m) || 0)) {
                              patch.replace_length_m = parseFloat(v.length_m) || 0;
                            }
                            update(i, patch);
                          }}
                          disabled={isLeave}
                          className={`px-1.5 py-1 border border-slate-200 rounded text-xs bg-white min-w-[14rem] ${isLeave ? 'opacity-50' : ''}`}
                        >
                          <option value="">— No replacement —</option>
                          {options.map(o => <option key={o.id} value={o.id}>{o.material} · {o.profile} — ${o.rate_per_m.toFixed(2)}/m</option>)}
                        </select>
                        {!isLeave && v.valley_option_id && (
                          <>
                            <RateDetailEditButton
                              component="valley"
                              rate={options.find(o => o.id === v.valley_option_id)}
                              testid={`valley-option-detail-${i}`}
                            />
                            <EditRateLink component="valley" rateId={v.valley_option_id} testid={`valley-option-edit-${i}`} label="✎" />
                          </>
                        )}
                      </div>
                    </td>
                    <td className="text-right font-mono-num font-semibold text-slate-800">{isLeave ? '—' : '$' + lineTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="text-xs font-bold text-slate-700 bg-slate-50 border-t-2 border-slate-300">
              <tr>
                <td className="py-1.5 font-mono-num">{replacements.length}</td>
                <td>Total</td>
                <td></td>
                <td className="text-right font-mono-num">{totalLengthAll.toFixed(2)}m</td>
                <td className="text-[10px] text-slate-500 font-normal italic">{replacements.filter(r => (r.action || 'replace') === 'leave').length} left as-is</td>
                <td className="text-right font-mono-num">{totalReplaceAll.toFixed(2)}m</td>
                <td></td>
                <td className="text-right font-mono-num">${totalLineAll.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}


// ── Aquaseals section ─────────────────────────────────────────────────

function AquasealSection({ cfg, options, pendingCount = 0, onChange, onJumpToPin, onOpenAnnotator, onConfirmPins }) {
  if (!cfg) return null;
  const items = cfg.items || [];
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...cfg, items: next });
  };
  const leftCount = items.filter(a => (a.action || 'replace').toLowerCase() === 'leave').length;
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="aquaseal-section">
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
          <Pipette size={14} /> Pipes &amp; Flues — Dektites
          <span className="text-xs text-slate-500">({items.length} pin{items.length === 1 ? '' : 's'} · auto-suggested lead Dektites{leftCount ? ` · ${leftCount} left as-is` : ''})</span>
          {pendingCount > 0 && (
            <span data-testid="aquaseal-pending-badge" className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded ring-1 ring-amber-300">⚠ {pendingCount} pending</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onOpenAnnotator && (
            <button
              type="button"
              data-testid="aquaseal-open-annotator"
              onClick={() => onOpenAnnotator('Pipe / Flue')}
              className="text-[11px] px-2 py-0.5 border border-[#D35400] text-[#D35400] bg-orange-50 hover:bg-[#D35400] hover:text-white rounded font-semibold transition-colors inline-flex items-center gap-1"
              title="Open the roof-plan annotator to edit Pipe / Flue pins. Closes back to here."
            ><Pencil size={10} /> Edit on diagram ↗</button>
          )}
          {pendingCount > 0 && onConfirmPins && (
            <button
              type="button"
              data-testid="aquaseal-confirm-all"
              onClick={() => onConfirmPins(null, true)}
              className="text-[11px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold shadow-sm"
              title={`Confirm all ${pendingCount} pending Pipe / Flue pin${pendingCount === 1 ? '' : 's'}`}
            >✓ Confirm all {pendingCount} pending</button>
          )}
        </div>
      </div>
      <div className="p-3">
        {items.length === 0 ? (
          <div className="text-xs text-slate-400 italic" data-testid="aquaseal-empty">
            No Pipe / Flue pins on the master diagram.
          </div>
        ) : (
          <div className="space-y-2" data-testid="aquaseal-card-list">
            {items.map((a, i) => (
              <AquasealRepairCardRow
                key={a.pin_id || a.raw_id || i}
                refId={a.pin_id}
                testidPrefix={`aquaseal-row-${i}`}
                pinId={a.pin_id}
                pinType={a.type || ''}
                diameterMm={a.diameter_mm || null}
                qty={a.qty || 0}
                useLead={!!a.use_lead}
                action={(a.action || 'replace').toLowerCase()}
                confirmed={!!a.confirmed}
                options={options}
                onUpdate={(patch) => update(i, patch)}
                onAction={(next) => update(i, { action: next })}
                onConfirm={onConfirmPins ? () => onConfirmPins([a.raw_id], true) : undefined}
                onUnconfirm={onConfirmPins ? () => onConfirmPins([a.raw_id], false) : undefined}
                onJumpToPin={onJumpToPin}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stage 2 — Paint / Recoat section ──────────────────────────────────

/**
 * Stage 2 Paint section. Two modes:
 *   • Full Paint System — multi-coat application driven by
 *     `paint_systems` collection rows. Modern Concrete = 4 stages
 *     (Sterilise & wash · Seal · 1 Hi-Build · 2 Colour). Old Concrete
 *     = 4 stages (Sterilise & wash · Seal · 2 Hi-Build · 2 Colour).
 *     Each coat has its own $/m² rate sourced from the canonical
 *     paint_system row.
 *   • 3-stage Recoat — alternate lighter top-up pulled from
 *     `component_rate_tables` rows under component='recoat' matching
 *     the operator's roof subtype (New Concrete 3 stage / Old
 *     Concrete 3 stage). Single rolled-up line at materials+labour
 *     $/m² × area.
 *
 * Area auto-links from Roof Details total m² — operator can override
 * inline. Optional per-coat opt-out (e.g. skip a Colour coat).
 *
 * Auto-save: every change PUTs the config and re-pulls the parent's
 * preview totals so the headline sub-total updates immediately.
 */
export function Stage2PaintSection({ api, appraisalId, onLinesChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [editingBreakdown, setEditingBreakdown] = useState(false);
  const debTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/restoration/appraisals/${appraisalId}/concrete-repairs/stage2`);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, [api, appraisalId]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (overrides = {}) => {
    if (!data) return;
    setBusy(true);
    setErr(null);
    try {
      const payload = { ...data.config, ...overrides };
      await api.put(`/restoration/appraisals/${appraisalId}/concrete-repairs/stage2`, payload);
      await load();
      if (onLinesChanged) await onLinesChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [api, appraisalId, data, load, onLinesChanged]);

  // Debounced area override save — operator types m² so we wait 600ms.
  const queueSave = (overrides) => {
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(() => save(overrides), 600);
  };

  if (!data) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-4 text-xs text-slate-500" data-testid="stage2-paint-loading">
        <Loader2 size={11} className="inline animate-spin mr-1" /> Loading Stage 2 paint config…
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-50 border border-rose-300 rounded-md p-3 text-xs text-rose-700 flex items-center gap-2" data-testid="stage2-paint-error">
        <AlertCircle size={12} /> Stage 2: {err}
      </div>
    );
  }
  const cfg = data.config;
  const subtype = data.subtype || '';
  const ps = data.paint_system;
  const recoatOpts = data.recoat_options || [];
  const mode = cfg.mode || 'none';
  const skipSet = new Set(cfg.skip_coats || []);
  const previewTotal = data.preview?.total || 0;
  const lineCount = data.preview?.line_count || 0;
  const effectiveArea = data.effective_area_m2 || 0;
  const inheritedFromRoof = (cfg.area_m2_override === null || cfg.area_m2_override === undefined || cfg.area_m2_override === '') && data.roof_m2 > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="stage2-paint-section">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-start gap-3 flex-wrap">
        <PaintBucket size={16} className="text-emerald-700 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
            Stage 2 — Paint System {subtype && <span className="text-xs font-normal text-slate-500">· {subtype}</span>}
            {ps && (
              <button
                type="button"
                data-testid="stage2-edit-breakdowns-btn"
                onClick={() => setEditingBreakdown(true)}
                title="Edit per-coat cost breakdown (Buy · Material · Labour · Contractor)"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-700 transition-all"
              >
                <Calculator size={10} /> Edit breakdowns
              </button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {ps ? ps.notes : 'No paint system mapped for this roof subtype.'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Paint sub-total</div>
          <div className="text-lg font-bold text-emerald-800 font-mono-num" data-testid="stage2-paint-total">${previewTotal.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500">{lineCount} line{lineCount === 1 ? '' : 's'} · {effectiveArea.toFixed(1)} m²</div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Mode picker + Area input */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Mode</span>
            <div className="inline-flex bg-slate-100 rounded p-0.5" role="tablist">
              {[
                { v: 'full',   label: 'Full Paint',  test: 'mode-full',   disabled: !ps },
                { v: 'recoat', label: '3-stage Recoat', test: 'mode-recoat', disabled: recoatOpts.length === 0 },
                { v: 'none',   label: 'Skip',        test: 'mode-none',   disabled: false },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  disabled={opt.disabled || busy}
                  data-testid={`stage2-${opt.test}`}
                  onClick={() => save({ mode: opt.v })}
                  className={`px-3 py-1 text-xs font-semibold rounded transition-all ${
                    mode === opt.v
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : opt.disabled
                        ? 'text-slate-300 cursor-not-allowed'
                        : 'text-slate-700 hover:bg-white'
                  }`}
                >{opt.label}</button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
              Area (m²) {inheritedFromRoof && <span className="text-emerald-600 normal-case font-normal" title="Auto-linked from Roof Details">· auto-linked</span>}
            </span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="0.1"
                min="0"
                value={cfg.area_m2_override ?? ''}
                placeholder={data.roof_m2 > 0 ? `auto: ${data.roof_m2.toFixed(1)}` : '0.0'}
                data-testid="stage2-area-override"
                onChange={(e) => {
                  const v = e.target.value;
                  setData(d => ({ ...d, config: { ...d.config, area_m2_override: v === '' ? null : parseFloat(v) } }));
                  queueSave({ area_m2_override: v === '' ? null : parseFloat(v) });
                }}
                className="w-24 px-2 py-1 text-xs border border-slate-300 rounded text-slate-900"
              />
              {cfg.area_m2_override !== null && cfg.area_m2_override !== undefined && cfg.area_m2_override !== '' && (
                <button
                  type="button"
                  data-testid="stage2-area-reset"
                  onClick={() => save({ area_m2_override: null })}
                  className="text-[10px] text-slate-500 hover:text-emerald-700 underline"
                  title="Clear override and use Roof Details total"
                >reset</button>
              )}
            </div>
          </div>
          {mode === 'recoat' && recoatOpts.length > 1 && (
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between gap-2">
                <span>Recoat row</span>
                <span className="flex items-center gap-1.5">
                  {cfg.recoat_rate_id && (
                    <RateDetailEditButton
                      component="sheet"
                      rate={recoatOpts.find(o => o.id === cfg.recoat_rate_id)}
                      testid="stage2-recoat-detail"
                    />
                  )}
                  {cfg.recoat_rate_id && <EditRateLink component="recoat" rateId={cfg.recoat_rate_id} testid="stage2-recoat-edit" />}
                </span>
              </span>
              <select
                data-testid="stage2-recoat-pick"
                value={cfg.recoat_rate_id || ''}
                onChange={(e) => save({ recoat_rate_id: e.target.value })}
                className="text-xs px-2 py-1 border border-slate-300 rounded bg-white"
              >
                {recoatOpts.map(o => (
                  <option key={o.id} value={o.id}>{o.name} · ${o.rate_per_m2.toFixed(2)}/m²</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Coat breakdown table */}
        {mode === 'full' && ps && (
          <table className="w-full text-xs" data-testid="stage2-coats-table">
            <thead className="text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-1.5 w-16">Use</th>
                <th className="text-left">Stage / Coat</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Area</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {(ps.coats || []).map((c, i) => {
                const skip = skipSet.has(c.label);
                const lineTotal = skip ? 0 : (effectiveArea * (c.rate_per_m2 || 0));
                return (
                  <tr key={i} className={`border-b border-slate-100 last:border-0 ${skip ? 'opacity-50' : ''}`} data-testid={`stage2-coat-row-${i}`}>
                    <td className="py-1.5">
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!skip}
                          data-testid={`stage2-coat-${i}-include`}
                          onChange={(e) => {
                            const next = new Set(skipSet);
                            if (e.target.checked) next.delete(c.label);
                            else next.add(c.label);
                            save({ skip_coats: Array.from(next) });
                          }}
                          className="rounded text-emerald-600 focus:ring-emerald-400"
                        />
                        <span className="text-[10px] text-slate-500">{skip ? 'skip' : 'include'}</span>
                      </label>
                    </td>
                    <td className="py-1.5">
                      <div className="font-medium text-slate-800">
                        {c.stage ? <span className="text-emerald-700 font-semibold">Stage {c.stage} · </span> : null}{c.label}
                      </div>
                      {c.desc && <div className="text-[11px] text-slate-400 italic">{c.desc}</div>}
                    </td>
                    <td className="text-right font-mono-num text-slate-600">${(c.rate_per_m2 || 0).toFixed(2)}/m²</td>
                    <td className="text-right font-mono-num text-slate-600">{effectiveArea.toFixed(1)} m²</td>
                    <td className="text-right font-mono-num font-semibold text-slate-800">${lineTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-bold">
                <td colSpan={4} className="py-2 text-right text-slate-700">Stage 2 Paint Total</td>
                <td className="py-2 text-right font-mono-num text-emerald-800">${previewTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        )}

        {mode === 'recoat' && recoatOpts.length > 0 && (
          <div className="bg-teal-50 border border-teal-200 rounded p-3" data-testid="stage2-recoat-breakdown">
            {(() => {
              const opt = recoatOpts.find(o => o.id === cfg.recoat_rate_id) || recoatOpts[0];
              const total = effectiveArea * (opt.rate_per_m2 || 0);
              return (
                <>
                  <div className="text-sm font-semibold text-teal-900">{opt.name}</div>
                  <div className="text-[11px] text-teal-700 italic mt-0.5">3-stage premium recoat — {opt.labour_desc || 'P.Sealer · Hi-Build · Top-coat'} · supplier {opt.supplier || 'BondLast'}</div>
                  <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                    <div><span className="text-slate-500">Rate</span> <span className="font-mono-num font-semibold">${opt.rate_per_m2.toFixed(2)}/m²</span></div>
                    <div><span className="text-slate-500">Material</span> <span className="font-mono-num">${opt.material_per_m2.toFixed(2)}</span></div>
                    <div><span className="text-slate-500">Labour</span> <span className="font-mono-num">${opt.labour_per_m2.toFixed(2)}</span></div>
                    <div className="text-right"><span className="text-slate-500">Total</span> <span className="font-mono-num font-bold text-teal-900">${total.toFixed(2)}</span></div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {mode === 'none' && (
          <div className="text-xs text-slate-500 italic py-2" data-testid="stage2-skipped">
            Stage 2 paint is currently skipped — no paint lines will appear in the quote.
          </div>
        )}

        {!ps && mode === 'full' && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="stage2-no-paint-system">
            <AlertCircle size={11} className="inline mr-1" /> No paint system mapped for "{subtype || '(no roof subtype)'}". Pick a Roof Subtype above (Modern Concrete / Old Concrete) to unlock Stage 2.
          </div>
        )}

        {busy && <div className="text-[11px] text-slate-500 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Saving…</div>}
      </div>

      {editingBreakdown && ps && (
        <PaintBreakdownModal
          api={api}
          paintSystem={ps}
          onClose={() => setEditingBreakdown(false)}
          onSaved={async () => {
            setEditingBreakdown(false);
            await load();
            if (onLinesChanged) await onLinesChanged();
          }}
        />
      )}
    </div>
  );
}


// ── Stage 2 paint — per-coat breakdown editor (modal) ─────────────────

/**
 * Mirror form of the Stage-2 Paint System table where each coat carries
 * a 4-column cost breakdown the office uses for margin / supplier
 * tracking:
 *   • Buy        — wholesale cost from supplier ($/m²)
 *   • Material   — sales rate billed for materials ($/m²)
 *   • Labour     — sales rate billed for labour    ($/m²)
 *   • Contractor — sub-contracted labour cost      ($/m²)
 *
 * Sales rate (the number shown on the read-only Stage-2 panel) is
 * computed as `Material + Labour`. Buy + Contractor are internal
 * cost columns — they flow into margin calcs but don't change the
 * quote price.
 *
 * Save calls the existing admin-only `PUT /paint-systems/{id}` so
 * the operator gets a single global rate-card edit (not per-job).
 * The parent re-pulls Stage-2 config after a successful save.
 */
export function PaintBreakdownModal({ api, paintSystem, onClose, onSaved }) {
  // Component Rate Tables row linked → Mat/Lab are priced from the table (read-only).
  const rateRow = paintSystem.rate_row || null;
  const pricedCoats = paintSystem.priced_coats || null;
  const [coats, setCoats] = useState(() =>
    (paintSystem.coats || []).map((c, ci) => ({
      ...c,
      buy_per_m2:        Number(c.buy_per_m2 || 0),
      material_per_m2:   Number((pricedCoats ? pricedCoats[ci]?.material_per_m2 : null) ?? c.material_per_m2 ?? c.rate_per_m2 ?? 0),
      labour_per_m2:     Number((pricedCoats ? pricedCoats[ci]?.labour_per_m2 : null) ?? c.labour_per_m2 ?? 0),
      contractor_per_m2: Number(c.contractor_per_m2 || 0),
      // Supply (volumetric) fields — operator-entered for Stages 2-4.
      material_name:           c.material_name || '',
      supplier:                c.supplier || '',
      coverage_m2_per_litre:   Number(c.coverage_m2_per_litre || 0),
      container_litres:        Number(c.container_litres || 0),
      container_price:         Number(c.container_price || 0),
      // Apply-time fields for the labour $/hr back-calculator.
      apply_hours:             Number(c.apply_hours || 0),
      apply_reference_m2:      Number(c.apply_reference_m2 || 0),
      // Supply mode — 'bucket' (volumetric paint) vs 'hourly'
      // (waterblaster, scaffold, other equipment with $/hr charge).
      supply_mode:             (c.supply_mode || 'bucket'),
      material_hourly_rate:    Number(c.material_hourly_rate || 0),
      // Mirror link — when set, this coat copies all breakdown /
      // supply / apply-time fields from another coat by label.
      mirror_of_label:         c.mirror_of_label || '',
      // stage_substage is deprecated — flat 1..N numbering only.
      stage_substage:          '',
    }))
  );
  // Synthetic view — resolves mirror links so the UI shows the
  // effective values (Coat 2 surfaces Coat 1's numbers when linked).
  const effective = useMemo(() => {
    const byLabel = {};
    coats.forEach(c => { if (c.label) byLabel[c.label.toLowerCase()] = c; });
    const SYNC = ['buy_per_m2','material_per_m2','labour_per_m2','contractor_per_m2',
                  'material_name','supplier','coverage_m2_per_litre','container_litres',
                  'container_price','supply_mode','material_hourly_rate',
                  'apply_hours','apply_reference_m2'];
    return coats.map(c => {
      const src = (c.mirror_of_label || '').toLowerCase();
      if (!src) return c;
      const source = byLabel[src];
      if (!source || source === c) return c;
      const merged = { ...c };
      SYNC.forEach(f => { merged[f] = source[f]; });
      return merged;
    });
  }, [coats]);
  // Track which coat rows have their supply panel expanded — open by
  // default whenever the row already carries supply data so the
  // operator immediately sees the calc.
  const [expanded, setExpanded] = useState(() => {
    const out = {};
    (paintSystem.coats || []).forEach((c, i) => {
      if (c.supplier || c.material_name || c.container_price || c.apply_hours) out[i] = true;
    });
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // ── Editable metadata (name / subtype / notes) ─────────────────────
  // The paint system's identity fields are captured as local state so
  // the user can rename before saving. The FK on appraisals uses the
  // paint_system_id — renames don't invalidate any historical link.
  const [systemName,  setSystemName]  = useState(paintSystem.system_name  || '');
  const [roofSubtype, setRoofSubtype] = useState(paintSystem.roof_subtype || '');
  const [notesText,   setNotesText]   = useState(paintSystem.notes        || '');

  // ── Copy-coats-from-library picker (T2) ─────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSystems, setPickerSystems] = useState([]);
  const [pickerSourceId, setPickerSourceId] = useState('');
  const [pickerSelected, setPickerSelected] = useState({});
  const [pickerLoading, setPickerLoading] = useState(false);
  // Where to insert the copied coats. `null` (default) = append at end.
  // Any 0..coats.length index means "insert after this row".
  const [pickerInsertAfter, setPickerInsertAfter] = useState(null);

  const openPicker = async (insertAfterIdx = null) => {
    setPickerInsertAfter(insertAfterIdx);
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const { data } = await api.get('/paint-systems');
      const list = Array.isArray(data) ? data : (data?.paint_systems || data?.systems || []);
      // Alias `_id` → `id` so the picker's id-based comparisons work
      // regardless of which endpoint shape the backend serialised into.
      setPickerSystems(list.map(s => ({ ...s, id: s.id || s._id })).filter(s => !s.archived_at && s.id !== paintSystem.id));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load systems');
    } finally {
      setPickerLoading(false);
    }
  };

  const applyPickerCoats = () => {
    const source = pickerSystems.find(s => (s.id || s._id) === pickerSourceId);
    if (!source) { setPickerOpen(false); return; }
    const toAdd = (source.coats || [])
      .map((c, idx) => ({ c, idx }))
      .filter(({ idx }) => pickerSelected[idx])
      .map(({ c }) => ({
        ...c,
        // Auto-tag imported coats with source system for traceability.
        label: `${c.label || 'Coat'} (from ${source.system_name})`,
        // Clear mirror link — the referenced coat may not exist here.
        mirror_of_label:       '',
        buy_per_m2:            Number(c.buy_per_m2 || 0),
        material_per_m2:       Number(c.material_per_m2 ?? c.rate_per_m2 ?? 0),
        labour_per_m2:         Number(c.labour_per_m2 || 0),
        contractor_per_m2:     Number(c.contractor_per_m2 || 0),
        material_name:         c.material_name || '',
        supplier:              c.supplier || '',
        coverage_m2_per_litre: Number(c.coverage_m2_per_litre || 0),
        container_litres:      Number(c.container_litres || 0),
        container_price:       Number(c.container_price || 0),
        apply_hours:           Number(c.apply_hours || 0),
        apply_reference_m2:    Number(c.apply_reference_m2 || 0),
        supply_mode:           c.supply_mode || 'bucket',
        material_hourly_rate:  Number(c.material_hourly_rate || 0),
        stage_substage:        '',
      }));
    if (toAdd.length === 0) { setPickerOpen(false); return; }
    // Where to slot the new coats — either appended at the end
    // (default) or spliced in after a specific existing coat.
    const insertIdx = pickerInsertAfter == null ? coats.length : (pickerInsertAfter + 1);
    setCoats(prev => {
      const next = prev.slice();
      next.splice(insertIdx, 0, ...toAdd);
      return next;
    });
    // Auto-expand the new rows so their imported supply data is visible.
    setExpanded(prev => {
      const shifted = {};
      // First, bump every existing expanded key ≥ insertIdx by toAdd.length.
      Object.entries(prev).forEach(([k, v]) => {
        const oi = Number(k);
        shifted[oi >= insertIdx ? oi + toAdd.length : oi] = v;
      });
      toAdd.forEach((_, i) => { shifted[insertIdx + i] = true; });
      return shifted;
    });
    setPickerOpen(false);
    setPickerSourceId('');
    setPickerSelected({});
    setPickerInsertAfter(null);
    toast.success(`Copied ${toAdd.length} coat${toAdd.length > 1 ? 's' : ''} from ${source.system_name}`);
  };

  // ── Missing-data audit (T3) ─────────────────────────────────────────
  // A coat "needs edit" if it's NOT a mirror AND is missing critical
  // pricing data. Mirrored coats inherit from source and are fine.
  const needsEditSet = useMemo(() => {
    const out = new Set();
    coats.forEach((c, i) => {
      if ((c.mirror_of_label || '').trim()) return;
      const hasProduct = !!(c.material_name || c.supplier);
      const mode = c.supply_mode || 'bucket';
      const hasSupply = mode === 'hourly'
        ? Number(c.material_hourly_rate || 0) > 0 && Number(c.apply_hours || 0) > 0
        : Number(c.container_price || 0) > 0 && Number(c.container_litres || 0) > 0;
      const hasRate = (Number(c.material_per_m2 || 0) + Number(c.labour_per_m2 || 0)) > 0;
      // Flag if EITHER no product info OR no supply-side data OR no sales rate.
      if (!hasProduct || !hasSupply || !hasRate) out.add(i);
    });
    return out;
  }, [coats]);

  const updateCoat = (idx, patch) => {
    setCoats(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const next = { ...c, ...patch };
      // Auto-derive buy_per_m2 (wholesale purchase cost) from supply
      // fields when all relevant inputs are set. Material stays
      // operator-controlled as the sales rate WITH MARKUP. Only
      // overwrite buy_per_m2 if the previous value was empty OR
      // matched the previous auto-derived value (manual overrides
      // survive).
      const supplyFieldChanged = ['coverage_m2_per_litre', 'container_litres', 'container_price',
                                  'supply_mode', 'material_hourly_rate',
                                  'apply_hours', 'apply_reference_m2'].some(k => k in patch);
      if (supplyFieldChanged) {
        const mode = (next.supply_mode || 'bucket');
        let derived = 0;
        let prevDerived = 0;
        if (mode === 'hourly') {
          const hr = Number(next.material_hourly_rate || 0);
          const aH = Number(next.apply_hours || 0);
          const aR = Number(next.apply_reference_m2 || 0);
          if (hr > 0 && aH > 0 && aR > 0) {
            derived = Number((hr * aH / aR).toFixed(4));
          }
          const pHr = Number(c.material_hourly_rate || 0);
          const pH  = Number(c.apply_hours || 0);
          const pR  = Number(c.apply_reference_m2 || 0);
          if (pHr > 0 && pH > 0 && pR > 0) {
            prevDerived = Number((pHr * pH / pR).toFixed(4));
          }
        } else {
          const cov = Number(next.coverage_m2_per_litre || 0);
          const lit = Number(next.container_litres || 0);
          const prc = Number(next.container_price || 0);
          if (cov > 0 && lit > 0 && prc > 0) {
            derived = Number((prc / lit / cov).toFixed(4));
          }
          const pCov = Number(c.coverage_m2_per_litre || 0);
          const pLit = Number(c.container_litres || 0);
          const pPrc = Number(c.container_price || 0);
          if (pCov > 0 && pLit > 0 && pPrc > 0) {
            prevDerived = Number((pPrc / pLit / pCov).toFixed(4));
          }
        }
        if (derived > 0) {
          const prevBuy = Number(c.buy_per_m2 || 0);
          if (prevBuy === 0 || Math.abs(prevBuy - prevDerived) < 0.01) {
            next.buy_per_m2 = derived;
          }
        }
      }
      return next;
    }));
  };
  const toggleExpand = (idx) => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  // ── Coat-order mutations (delete / reorder / insert) ───────────────
  // The `expanded` map is keyed by index — so any structural change
  // to `coats` needs to remap the flags too so the +/− toggle state
  // follows the correct row after the change.
  const remapExpandedByOldIndex = (fromIdx, mapper) => {
    setExpanded(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const oi = Number(k);
        const ni = mapper(oi);
        if (ni != null && ni >= 0) next[ni] = v;
      });
      return next;
    });
  };

  const deleteCoat = (idx) => {
    const label = coats[idx]?.label || `Coat ${idx + 1}`;
    if (!window.confirm(`Delete "${label}"?  This cannot be undone until you Cancel out of the modal.`)) return;
    setCoats(prev => prev.filter((_, i) => i !== idx));
    remapExpandedByOldIndex(idx, (oi) => (oi === idx ? null : (oi > idx ? oi - 1 : oi)));
    // If any coat mirrors the deleted one, clear the link.
    const deletedLabel = (label || '').toLowerCase();
    setCoats(prev => prev.map(c => (
      (c.mirror_of_label || '').toLowerCase() === deletedLabel
        ? { ...c, mirror_of_label: '' }
        : c
    )));
  };

  const moveCoat = (idx, dir) => {
    const j = idx + dir;
    setCoats(prev => {
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      const [row] = next.splice(idx, 1);
      next.splice(j, 0, row);
      return next;
    });
    // Swap the expanded flags too so the +/− state travels with each row.
    remapExpandedByOldIndex(idx, (oi) => {
      if (oi === idx) return j;
      if (oi === j)   return idx;
      return oi;
    });
  };

  // Blueprint used by "+ Add blank coat" and by the Copy picker
  // when the user asks to import into a specific slot.
  const blankCoat = () => ({
    label:                 'New coat',
    stage:                 (coats[coats.length - 1]?.stage || 0) + 1,
    stage_substage:        '',
    desc:                  '',
    optional:              false,
    buy_per_m2:            0,
    material_per_m2:       0,
    labour_per_m2:         0,
    contractor_per_m2:     0,
    material_name:         '',
    supplier:              '',
    coverage_m2_per_litre: 0,
    container_litres:      0,
    container_price:       0,
    apply_hours:           0,
    apply_reference_m2:    0,
    supply_mode:           'bucket',
    material_hourly_rate:  0,
    mirror_of_label:       '',
  });

  const insertBlankAfter = (idx) => {
    setCoats(prev => {
      const next = prev.slice();
      next.splice(idx + 1, 0, blankCoat());
      return next;
    });
    // Nudge downstream expanded flags by 1.
    remapExpandedByOldIndex(-1, (oi) => (oi > idx ? oi + 1 : oi));
    // Auto-expand the new row so it's immediately editable.
    setExpanded(prev => ({ ...prev, [idx + 1]: true }));
  };

  const appendBlank = () => insertBlankAfter(coats.length - 1);

  // Re-sequence stage numbers based on current row order. Each coat
  // gets its own stage number by position (1..N). Sub-stages are no
  // longer used — the catalogue is flat.
  const renumberStages = () => {
    setCoats(prev => prev.map((c, i) => ({ ...c, stage: i + 1, stage_substage: '' })));
    toast.success('Stages renumbered');
  };

  const computedRate = (c) => Number(c.material_per_m2 || 0) + Number(c.labour_per_m2 || 0);
  const totals = effective.reduce((acc, c) => {
    acc.buy        += Number(c.buy_per_m2 || 0);
    acc.material   += Number(c.material_per_m2 || 0);
    acc.labour     += Number(c.labour_per_m2 || 0);
    acc.contractor += Number(c.contractor_per_m2 || 0);
    acc.rate       += computedRate(c);
    return acc;
  }, { buy: 0, material: 0, labour: 0, contractor: 0, rate: 0 });

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.put(`/paint-systems/${paintSystem.id}`, {
        roof_subtype: roofSubtype,
        system_name:  systemName.trim() || paintSystem.system_name,
        notes:        notesText,
        coats:        coats.map(c => ({
          label:             c.label,
          stage:             c.stage,
          desc:              c.desc,
          optional:          c.optional || false,
          buy_per_m2:        Number(c.buy_per_m2 || 0),
          material_per_m2:   Number(c.material_per_m2 || 0),
          labour_per_m2:     Number(c.labour_per_m2 || 0),
          contractor_per_m2: Number(c.contractor_per_m2 || 0),
          material_name:     c.material_name || '',
          supplier:          c.supplier || '',
          coverage_m2_per_litre: Number(c.coverage_m2_per_litre || 0),
          container_litres:  Number(c.container_litres || 0),
          container_price:   Number(c.container_price || 0),
          apply_hours:       Number(c.apply_hours || 0),
          apply_reference_m2: Number(c.apply_reference_m2 || 0),
          supply_mode:       c.supply_mode || 'bucket',
          material_hourly_rate: Number(c.material_hourly_rate || 0),
          mirror_of_label:   c.mirror_of_label || '',
          stage_substage:    c.stage_substage || '',
        })),
      });
      if (onSaved) await onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}
         data-testid="paint-breakdown-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Edit per-coat breakdown</div>
            {rateRow && (
              <div data-testid="paint-breakdown-priced-banner" className="mt-1 text-[11px] text-emerald-900 bg-white/70 border border-emerald-200 rounded px-2 py-1">
                <strong>Priced from Component Rate Tables</strong> → {rateRow.name}: Mat ${Number(rateRow.materials_rate).toFixed(2)} + Lab ${Number(rateRow.labour_rate).toFixed(2)} = <strong>${Number(rateRow.rtr_rate).toFixed(2)}/{rateRow.unit}</strong>.
                {' '}Coat Material/Labour $ are read-only (split of that rate).{' '}
                <a href="/component-rates?component=recoat" className="underline font-semibold">Edit rates →</a>
              </div>
            )}
            {/* Editable name — click-to-edit input. Rename is safe:
                appraisals reference this system by id, not by name. */}
            <div className="flex items-center gap-2 mt-1">
              <Calculator size={18} className="text-emerald-700 flex-shrink-0" />
              <input
                type="text"
                value={systemName}
                onChange={(e) => setSystemName(e.target.value)}
                data-testid="paint-breakdown-system-name-input"
                placeholder="System name"
                className="flex-1 text-lg font-bold text-slate-900 bg-transparent border-b border-transparent hover:border-emerald-300 focus:border-emerald-500 focus:outline-none px-1 py-0.5"
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              <label className="flex items-center gap-1.5 text-[11px]">
                <span className="uppercase tracking-wider font-bold text-slate-500">Roof subtype:</span>
                <select
                  value={roofSubtype}
                  onChange={(e) => setRoofSubtype(e.target.value)}
                  data-testid="paint-breakdown-roof-subtype-input"
                  className="text-[11px] border border-slate-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
                >
                  {['Modern Concrete', 'Old Concrete', 'Iron', 'Long-Run', 'Decramastic', 'Colortile', 'Clay', 'Gutter', 'Fascia', 'Garage Door', 'Generic', 'Other'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              {(systemName !== paintSystem.system_name || roofSubtype !== paintSystem.roof_subtype) && (
                <span
                  className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5"
                  title="Unsaved rename — click Save breakdowns to persist"
                  data-testid="paint-breakdown-rename-badge"
                >✎ Rename pending — historical quotes keep the old name</span>
              )}
            </div>
            <input
              type="text"
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              data-testid="paint-breakdown-notes-input"
              placeholder="Short description shown in the Stage 2 header"
              className="w-full text-[11px] text-slate-500 mt-1.5 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-emerald-500 focus:outline-none px-1 py-0.5"
            />
            <div className="text-[11px] text-slate-600 mt-1">
              Sales rate per m² = <strong className="text-emerald-800">Material + Labour</strong>.
              Buy is the wholesale purchase cost (auto-filled from the supply panel).
              Contractor is sub-contracted labour cost. Both internal — they don't change the quote.
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="paint-breakdown-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {/* Audit banner — surfaces coats missing critical data. */}
          {needsEditSet.size > 0 && (
            <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded border border-amber-300 bg-amber-50 text-[11px] text-amber-900" data-testid="paint-breakdown-audit-banner">
              <AlertCircle size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <strong>{needsEditSet.size} coat{needsEditSet.size > 1 ? 's' : ''} need attention</strong> — missing product, supplier, or supply-side data.
                Use <em>+ Copy coats from library</em> to pull from a populated system, or edit the flagged rows inline (yellow chip).
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="paint-breakdown-table">
              <thead className="text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pl-2 w-6"></th>
                  <th className="text-left py-2 pr-2 w-20">Stage</th>
                  <th className="text-left py-2 pr-2">Coat</th>
                  <th className="text-right py-2 px-1.5 w-24" title="Wholesale supplier cost per m²">Buy $/m²</th>
                  <th className="text-right py-2 px-1.5 w-24" title="Sales rate billed for materials">Material $/m²</th>
                  <th className="text-right py-2 px-1.5 w-24" title="Sales rate billed for labour">Labour $/m²</th>
                  <th className="text-right py-2 px-1.5 w-24" title="Sub-contracted labour cost">Contractor $/m²</th>
                  <th className="text-right py-2 pl-2 w-28 bg-emerald-50">Rate (sales)</th>
                  <th className="text-center py-2 px-2 w-28" title="Reorder / insert / delete">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coats.map((rawCoat, i) => {
                  const c = effective[i];
                  const isMirrored = !!(rawCoat.mirror_of_label || '').trim();
                  const cov = Number(c.coverage_m2_per_litre || 0);
                  const lit = Number(c.container_litres || 0);
                  const prc = Number(c.container_price || 0);
                  const mode = (c.supply_mode || 'bucket');
                  const hrly = Number(c.material_hourly_rate || 0);
                  let supplyComputed = 0;
                  // Labour $/hr back-calc — operator records how
                  // long (apply_hours) it takes to apply this coat
                  // over a known area (apply_reference_m2). We then
                  // surface implied hourly rates: $/hr = $/m² ×
                  // (reference_m2 / apply_hours).
                  const appH = Number(c.apply_hours || 0);
                  const appRef = Number(c.apply_reference_m2 || 0);
                  const throughput = (appH > 0 && appRef > 0) ? (appRef / appH) : 0;
                  if (mode === 'hourly') {
                    supplyComputed = (hrly > 0 && throughput > 0) ? (hrly / throughput) : 0;
                  } else if (cov > 0 && lit > 0 && prc > 0) {
                    supplyComputed = prc / lit / cov;
                  }
                  const labourPerHr = throughput * Number(c.labour_per_m2 || 0);
                  const contractorPerHr = throughput * Number(c.contractor_per_m2 || 0);
                  const isOpen = !!expanded[i];
                  const hasSupply = c.material_name || c.supplier || supplyComputed > 0 || appH > 0 || hrly > 0;
                  return (
                    <React.Fragment key={i}>
                      <tr className="border-b border-slate-100 last:border-0" data-testid={`paint-breakdown-row-${i}`}>
                        <td className="py-1.5 pl-2">
                          <button
                            type="button"
                            data-testid={`paint-breakdown-toggle-${i}`}
                            onClick={() => toggleExpand(i)}
                            title={isOpen ? 'Hide supply inputs' : 'Show / add supply inputs (volumetric paint coats)'}
                            className={`w-5 h-5 inline-flex items-center justify-center rounded text-xs font-bold transition-all ${
                              hasSupply
                                ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                            }`}
                          >{isOpen ? '−' : '+'}</button>
                        </td>
                        <td className="py-1.5 pr-2">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Stage {c.stage || '—'}
                          </span>
                        </td>
                        <td className="py-1.5 pr-2">
                          <div className="font-medium text-slate-800 flex items-center gap-1.5 flex-wrap">
                            {c.label}
                            {needsEditSet.has(i) && (
                              <span
                                className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300"
                                title="Missing product / supplier / supply data — click + to expand and fill in"
                                data-testid={`paint-breakdown-${i}-needs-edit-chip`}
                              >
                                ⚠ Needs edit
                              </span>
                            )}
                            {isMirrored && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200" title={`Values mirrored from "${rawCoat.mirror_of_label}"`} data-testid={`paint-breakdown-${i}-mirror-badge`}>
                                ↺ Mirrors {rawCoat.mirror_of_label}
                              </span>
                            )}
                            {/* Mirror toggle — only shown when there's
                                an obvious matching source coat. */}
                            {(() => {
                              if (!c.label) return null;
                              // Heuristic: a coat is mirrorable when
                              // another coat in the list shares the
                              // same stage AND the label differs only
                              // by a trailing number (e.g. "Colour
                              // Coat 1" → "Colour Coat 2").
                              const base = c.label.replace(/\s+\d+$/, '').trim();
                              const candidates = coats.filter((other, j) =>
                                j !== i &&
                                other.label &&
                                other.stage === c.stage &&
                                other.label.replace(/\s+\d+$/, '').trim() === base
                              );
                              if (candidates.length === 0) return null;
                              const src = candidates[0].label;
                              const linked = (rawCoat.mirror_of_label || '').toLowerCase() === src.toLowerCase();
                              return (
                                <button
                                  type="button"
                                  data-testid={`paint-breakdown-${i}-mirror-toggle`}
                                  onClick={() => updateCoat(i, { mirror_of_label: linked ? '' : src })}
                                  className={`text-[10px] px-1.5 py-0.5 rounded border transition-all ${
                                    linked
                                      ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                                      : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                                  }`}
                                  title={linked ? `Unlink from "${src}" so this coat can be edited independently` : `Link to "${src}" so this coat auto-syncs its values`}
                                >
                                  {linked ? '🔓 Unlink' : `🔗 Mirror ${src}`}
                                </button>
                              );
                            })()}
                          </div>
                          {c.desc && <div className="text-[11px] text-slate-400 italic">{c.desc}</div>}
                          {hasSupply && !isOpen && (
                            <div className="text-[10px] text-teal-700 mt-0.5">
                              <strong>{c.material_name || 'material'}</strong>
                              {c.supplier && <span> · {c.supplier}</span>}
                              {supplyComputed > 0 && <span> · {cov}m²/L · {lit}L @ ${prc.toFixed(2)}</span>}
                            </div>
                          )}
                        </td>
                        {['buy_per_m2', 'material_per_m2', 'labour_per_m2', 'contractor_per_m2'].map((field) => (
                          <td key={field} className="py-1.5 px-1.5">
                            <div className="relative">
                              <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={c[field] ?? 0}
                                disabled={isMirrored || (rateRow && (field === 'material_per_m2' || field === 'labour_per_m2'))}
                                title={rateRow && (field === 'material_per_m2' || field === 'labour_per_m2') ? `Priced from Component Rate Tables → ${rateRow.name}` : undefined}
                                data-testid={`paint-breakdown-${i}-${field}`}
                                onChange={(e) => updateCoat(i, { [field]: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                className={`w-full pl-4 pr-1.5 py-1 text-xs border rounded text-right font-mono-num focus:outline-none focus:ring-1 ${
                                  isMirrored
                                    ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed'
                                    : 'border-slate-300 focus:ring-emerald-400 focus:border-emerald-400'
                                }`}
                              />
                            </div>
                          </td>
                        ))}
                        <td className="py-1.5 pl-2 text-right font-mono-num font-bold text-emerald-800 bg-emerald-50" data-testid={`paint-breakdown-${i}-rate`}>
                          ${computedRate(c).toFixed(2)}
                        </td>
                        <td className="py-1.5 px-2 text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => moveCoat(i, -1)}
                            disabled={i === 0}
                            data-testid={`paint-breakdown-${i}-move-up`}
                            title="Move up"
                            className="w-6 h-6 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronUp size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveCoat(i, 1)}
                            disabled={i === coats.length - 1}
                            data-testid={`paint-breakdown-${i}-move-down`}
                            title="Move down"
                            className="w-6 h-6 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronDown size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => insertBlankAfter(i)}
                            data-testid={`paint-breakdown-${i}-insert-below`}
                            title="Insert new blank coat below this one"
                            className="w-6 h-6 inline-flex items-center justify-center rounded text-emerald-600 hover:bg-emerald-100 hover:text-emerald-800"
                          >
                            <Plus size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteCoat(i)}
                            data-testid={`paint-breakdown-${i}-delete`}
                            title="Delete this coat"
                            className="w-6 h-6 inline-flex items-center justify-center rounded text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="border-b border-slate-100 last:border-0 bg-teal-50/40" data-testid={`paint-breakdown-supply-${i}`}>
                          <td></td>
                          <td colSpan={8} className="py-2 pr-2 pl-2">
                            {isMirrored && (
                              <div className="mb-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1.5">
                                <span className="font-bold">↺ Mirrored</span>
                                Supply &amp; apply-time inputs are read-only while this coat is linked to <strong>"{rawCoat.mirror_of_label}"</strong>. Click "Unlink" above to edit independently.
                              </div>
                            )}
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700">
                                Material supply · auto-computes Buy $/m²
                              </div>
                              <div className="inline-flex bg-white border border-teal-200 rounded p-0.5" data-testid={`paint-breakdown-${i}-mode-toggle`}>
                                {[
                                  { v: 'bucket', label: 'Bucket / Volumetric' },
                                  { v: 'hourly', label: 'Hourly Equipment' },
                                ].map((opt) => (
                                  <button
                                    key={opt.v}
                                    type="button"
                                    disabled={isMirrored}
                                    data-testid={`paint-breakdown-${i}-mode-${opt.v}`}
                                    onClick={() => updateCoat(i, { supply_mode: opt.v })}
                                    className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-all ${
                                      mode === opt.v
                                        ? 'bg-teal-600 text-white shadow-sm'
                                        : isMirrored ? 'text-slate-300 cursor-not-allowed' : 'text-teal-700 hover:bg-teal-50'
                                    }`}
                                  >{opt.label}</button>
                                ))}
                              </div>
                            </div>
                            {mode === 'hourly' ? (
                              <div className="grid grid-cols-12 gap-2 items-end">
                                <label className="col-span-4 flex flex-col gap-0.5">
                                  <span className="text-[10px] text-slate-500 font-semibold">Equipment / material name</span>
                                  <input
                                    type="text"
                                    value={c.material_name || ''}
                                    disabled={isMirrored}
                                    data-testid={`paint-breakdown-${i}-material_name`}
                                    onChange={(e) => updateCoat(i, { material_name: e.target.value })}
                                    className={`px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                    placeholder="e.g. Waterblaster"
                                  />
                                </label>
                                <label className="col-span-3 flex flex-col gap-0.5">
                                  <span className="text-[10px] text-slate-500 font-semibold">Supplier / source</span>
                                  <input
                                    type="text"
                                    value={c.supplier || ''}
                                    disabled={isMirrored}
                                    data-testid={`paint-breakdown-${i}-supplier`}
                                    onChange={(e) => updateCoat(i, { supplier: e.target.value })}
                                    className={`px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                    placeholder="optional"
                                  />
                                </label>
                                <label className="col-span-3 flex flex-col gap-0.5">
                                  <span className="text-[10px] text-slate-500 font-semibold">Charge ($/hr)</span>
                                  <div className="relative">
                                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">$</span>
                                    <input
                                      type="number" step="0.01" min="0"
                                      value={c.material_hourly_rate || 0}
                                      disabled={isMirrored}
                                      data-testid={`paint-breakdown-${i}-material_hourly_rate`}
                                      onChange={(e) => updateCoat(i, { material_hourly_rate: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                      className={`w-full pl-4 pr-1.5 py-1 text-xs border rounded text-right font-mono-num focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                      placeholder="e.g. 100"
                                    />
                                  </div>
                                </label>
                                <div className="col-span-2 text-right">
                                  <div className="text-[10px] text-slate-500 font-semibold">$/m²</div>
                                  <div className="text-sm font-bold text-teal-800 font-mono-num" data-testid={`paint-breakdown-${i}-supply-derived`}>
                                    ${supplyComputed.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="grid grid-cols-12 gap-2 items-end">
                              <label className="col-span-3 flex flex-col gap-0.5">
                                <span className="text-[10px] text-slate-500 font-semibold">Material name</span>
                                <input
                                  type="text"
                                  value={c.material_name || ''}
                                  disabled={isMirrored}
                                  data-testid={`paint-breakdown-${i}-material_name`}
                                  onChange={(e) => updateCoat(i, { material_name: e.target.value })}
                                  className={`px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                  placeholder="e.g. Key Coat Sealer"
                                />
                              </label>
                              <label className="col-span-2 flex flex-col gap-0.5">
                                <span className="text-[10px] text-slate-500 font-semibold">Supplier</span>
                                <input
                                  type="text"
                                  value={c.supplier || ''}
                                  disabled={isMirrored}
                                  data-testid={`paint-breakdown-${i}-supplier`}
                                  onChange={(e) => updateCoat(i, { supplier: e.target.value })}
                                  className={`px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                  placeholder="e.g. Paintek"
                                />
                              </label>
                              <label className="col-span-2 flex flex-col gap-0.5">
                                <span className="text-[10px] text-slate-500 font-semibold" title="Square metres covered per litre of paint">Coverage (m²/L)</span>
                                <input
                                  type="number" step="0.1" min="0"
                                  value={c.coverage_m2_per_litre || 0}
                                  disabled={isMirrored}
                                  data-testid={`paint-breakdown-${i}-coverage_m2_per_litre`}
                                  onChange={(e) => updateCoat(i, { coverage_m2_per_litre: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                  className={`px-2 py-1 text-xs border rounded text-right font-mono-num focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                />
                              </label>
                              <label className="col-span-2 flex flex-col gap-0.5">
                                <span className="text-[10px] text-slate-500 font-semibold">Container size (L)</span>
                                <input
                                  type="number" step="0.1" min="0"
                                  value={c.container_litres || 0}
                                  disabled={isMirrored}
                                  data-testid={`paint-breakdown-${i}-container_litres`}
                                  onChange={(e) => updateCoat(i, { container_litres: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                  className={`px-2 py-1 text-xs border rounded text-right font-mono-num focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                />
                              </label>
                              <label className="col-span-2 flex flex-col gap-0.5">
                                <span className="text-[10px] text-slate-500 font-semibold">Container price ($)</span>
                                <div className="relative">
                                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">$</span>
                                  <input
                                    type="number" step="0.01" min="0"
                                    value={c.container_price || 0}
                                    disabled={isMirrored}
                                    data-testid={`paint-breakdown-${i}-container_price`}
                                    onChange={(e) => updateCoat(i, { container_price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                    className={`w-full pl-4 pr-1.5 py-1 text-xs border rounded text-right font-mono-num focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                  />
                                </div>
                              </label>
                              <div className="col-span-1 text-right">
                                <div className="text-[10px] text-slate-500 font-semibold">$/m²</div>
                                <div className="text-sm font-bold text-teal-800 font-mono-num" data-testid={`paint-breakdown-${i}-supply-derived`}>
                                  ${supplyComputed.toFixed(2)}
                                </div>
                              </div>
                            </div>
                            )}
                            {supplyComputed > 0 && (
                              <div className="text-[10px] text-slate-500 mt-1.5">
                                {mode === 'hourly' ? (
                                  <>${hrly.toFixed(2)}/hr × {appH} hr ÷ {appRef} m² = <strong className="text-teal-800">${supplyComputed.toFixed(4)}/m²</strong></>
                                ) : (
                                  <>${prc.toFixed(2)} ÷ {lit}L ÷ {cov} m²/L = <strong className="text-teal-800">${supplyComputed.toFixed(4)}/m²</strong></>
                                )}
                                {' '}· auto-fills <strong>Buy $/m²</strong> above (wholesale cost). Material $/m² stays manual — enter the sales rate with your markup.
                              </div>
                            )}

                            <div className="mt-3 pt-2 border-t border-teal-200/60">
                              <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700 mb-1.5">
                                Labour throughput · back-calc $/hr from time-on-roof
                              </div>
                              <div className="grid grid-cols-12 gap-2 items-end">
                                <label className="col-span-2 flex flex-col gap-0.5">
                                  <span className="text-[10px] text-slate-500 font-semibold">Apply time (hrs)</span>
                                  <input
                                    type="number" step="0.1" min="0"
                                    value={c.apply_hours || 0}
                                    disabled={isMirrored}
                                    data-testid={`paint-breakdown-${i}-apply_hours`}
                                    onChange={(e) => updateCoat(i, { apply_hours: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                    className={`px-2 py-1 text-xs border rounded text-right font-mono-num focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                    placeholder="e.g. 2"
                                  />
                                </label>
                                <label className="col-span-2 flex flex-col gap-0.5">
                                  <span className="text-[10px] text-slate-500 font-semibold">Reference area (m²)</span>
                                  <input
                                    type="number" step="1" min="0"
                                    value={c.apply_reference_m2 || 0}
                                    disabled={isMirrored}
                                    data-testid={`paint-breakdown-${i}-apply_reference_m2`}
                                    onChange={(e) => updateCoat(i, { apply_reference_m2: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                    className={`px-2 py-1 text-xs border rounded text-right font-mono-num focus:outline-none focus:ring-1 ${isMirrored ? 'bg-amber-50/40 border-amber-200 text-amber-800 cursor-not-allowed' : 'border-slate-300 focus:ring-teal-400'}`}
                                    placeholder="e.g. 200"
                                  />
                                </label>
                                <div className="col-span-2 text-center">
                                  <div className="text-[10px] text-slate-500 font-semibold">Throughput</div>
                                  <div className="text-sm font-bold text-teal-800 font-mono-num" data-testid={`paint-breakdown-${i}-throughput`}>
                                    {throughput > 0 ? `${throughput.toFixed(0)} m²/hr` : '—'}
                                  </div>
                                </div>
                                <div className="col-span-3 text-center bg-teal-100/70 rounded px-2 py-1">
                                  <div className="text-[10px] text-slate-600 font-semibold">Labour $/hr</div>
                                  <div className="text-sm font-bold text-teal-900 font-mono-num" data-testid={`paint-breakdown-${i}-labour-per-hr`}>
                                    {labourPerHr > 0 ? `$${labourPerHr.toFixed(2)}/hr` : '—'}
                                  </div>
                                  <div className="text-[9px] text-slate-500">${Number(c.labour_per_m2 || 0).toFixed(2)}/m² × {throughput > 0 ? throughput.toFixed(0) : '—'} m²/hr</div>
                                </div>
                                <div className="col-span-3 text-center bg-teal-100/70 rounded px-2 py-1">
                                  <div className="text-[10px] text-slate-600 font-semibold">Contractor $/hr</div>
                                  <div className="text-sm font-bold text-teal-900 font-mono-num" data-testid={`paint-breakdown-${i}-contractor-per-hr`}>
                                    {contractorPerHr > 0 ? `$${contractorPerHr.toFixed(2)}/hr` : '—'}
                                  </div>
                                  <div className="text-[9px] text-slate-500">${Number(c.contractor_per_m2 || 0).toFixed(2)}/m² × {throughput > 0 ? throughput.toFixed(0) : '—'} m²/hr</div>
                                </div>
                              </div>
                              {throughput > 0 && (Number(c.labour_per_m2 || 0) > 0 || Number(c.contractor_per_m2 || 0) > 0) && (
                                <div className="text-[10px] text-slate-500 mt-1.5">
                                  {appRef} m² ÷ {appH} hr = <strong className="text-teal-800">{throughput.toFixed(0)} m²/hr</strong>.
                                  {Number(c.labour_per_m2 || 0) > 0 && <> Labour: ${(Number(c.labour_per_m2) * appRef).toFixed(2)} / {appH} hr = <strong>${labourPerHr.toFixed(2)}/hr</strong>.</>}
                                  {Number(c.contractor_per_m2 || 0) > 0 && <> Contractor: ${(Number(c.contractor_per_m2) * appRef).toFixed(2)} / {appH} hr = <strong>${contractorPerHr.toFixed(2)}/hr</strong>.</>}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-bold bg-slate-50">
                  <td colSpan={3} className="py-2 pl-2 text-right text-slate-700">Per-m² totals</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${totals.buy.toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${totals.material.toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${totals.labour.toFixed(2)}</td>
                  <td className="text-right py-2 px-1.5 font-mono-num text-slate-700">${totals.contractor.toFixed(2)}</td>
                  <td className="text-right py-2 pl-2 font-mono-num text-emerald-900 bg-emerald-100" data-testid="paint-breakdown-total-rate">${totals.rate.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {err && (
            <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1.5" data-testid="paint-breakdown-error">
              <AlertCircle size={12} /> {err}
            </div>
          )}

          <div className="mt-4 text-[11px] text-slate-500 italic">
            Note: this updates the canonical paint system used by <strong>every</strong> appraisal of this roof subtype.
            Changes apply immediately to every Restoration quote on the same paint system.
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => openPicker(null)}
            data-testid="paint-breakdown-copy-coats-btn"
            className="mr-auto inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-white border border-emerald-300 rounded hover:bg-emerald-600 hover:text-white hover:border-emerald-700 transition-colors"
          >
            <Copy size={11} /> Copy coats from library…
          </button>
          <button
            type="button"
            onClick={renumberStages}
            disabled={coats.length === 0}
            data-testid="paint-breakdown-renumber-stages"
            title="Re-sequence stage numbers 1..N based on row order"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-600 hover:text-white hover:border-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Layers size={11} /> Renumber stages
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="paint-breakdown-cancel"
            className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded"
          >Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="paint-breakdown-save"
            className="px-4 py-1.5 text-xs font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            {saving ? 'Saving…' : 'Save breakdowns'}
          </button>
        </div>

        {/* Copy-coats-from-library picker (T2) */}
        {pickerOpen && (
          <div
            className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => setPickerOpen(false)}
            data-testid="paint-breakdown-picker-overlay"
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-full max-w-2xl my-12"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-3 border-b border-slate-200 bg-emerald-50 flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Copy coats from library</div>
                  <div className="text-sm text-slate-700 mt-0.5">Pull one or more coats from another paint system to seed this one.</div>
                </div>
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  data-testid="paint-breakdown-picker-close"
                  className="text-slate-400 hover:text-slate-700"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Source paint system</span>
                  <select
                    value={pickerSourceId}
                    onChange={(e) => { setPickerSourceId(e.target.value); setPickerSelected({}); }}
                    data-testid="paint-breakdown-picker-source"
                    className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  >
                    <option value="">— pick a system —</option>
                    {pickerSystems.map((s) => {
                      const populatedCount = (s.coats || []).filter(c => c.supplier || c.material_name || c.container_price || c.material_hourly_rate).length;
                      const totalCount = (s.coats || []).length;
                      return (
                        <option key={s.id || s._id} value={s.id || s._id}>
                          {s.system_name} ({s.roof_subtype}) — {populatedCount}/{totalCount} coats populated
                        </option>
                      );
                    })}
                  </select>
                </label>
                {pickerLoading ? (
                  <div className="text-center py-4 text-xs text-slate-500">
                    <Loader2 size={14} className="inline animate-spin mr-2" /> Loading systems…
                  </div>
                ) : pickerSourceId && (() => {
                  const src = pickerSystems.find(s => (s.id || s._id) === pickerSourceId);
                  if (!src) return null;
                  const coatList = src.coats || [];
                  return (
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Select coats to copy</div>
                      <div className="border border-slate-200 rounded max-h-72 overflow-y-auto">
                        {coatList.length === 0 ? (
                          <div className="p-4 text-center text-xs text-slate-400">No coats defined on this system.</div>
                        ) : coatList.map((c, idx) => {
                          const isPopulated = !!(c.supplier || c.material_name || c.container_price || c.material_hourly_rate);
                          const sell = Number(c.material_per_m2 || 0) + Number(c.labour_per_m2 || 0);
                          return (
                            <label
                              key={idx}
                              className="flex items-center gap-2 p-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
                              data-testid={`paint-breakdown-picker-coat-${idx}`}
                            >
                              <input
                                type="checkbox"
                                checked={!!pickerSelected[idx]}
                                onChange={(e) => setPickerSelected(prev => ({ ...prev, [idx]: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              <div className="flex-1 text-xs">
                                <div className="font-semibold text-slate-800">
                            Stage {c.stage || '—'} · {c.label}
                                </div>
                                <div className="text-[10px] text-slate-500">
                                  {c.material_name || <em className="text-slate-400">no product</em>}
                                  {c.supplier ? ` · ${c.supplier}` : ''}
                                </div>
                              </div>
                              <div className="text-[10px] text-right">
                                <div className="text-emerald-700 font-bold font-mono-num">${sell.toFixed(2)}/m²</div>
                                <div className={`text-[9px] font-semibold uppercase ${isPopulated ? 'text-emerald-600' : 'text-amber-600'}`}>
                                  {isPopulated ? 'Populated' : 'Placeholder'}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-2">
                        Selected coats will be appended with labels like <em>&quot;{coatList[0]?.label || 'X'} (from {src.system_name})&quot;</em>.
                        Mirror links are cleared on copy.
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded"
                  data-testid="paint-breakdown-picker-cancel"
                >Cancel</button>
                <button
                  type="button"
                  onClick={applyPickerCoats}
                  disabled={!pickerSourceId || Object.values(pickerSelected).filter(Boolean).length === 0}
                  data-testid="paint-breakdown-picker-apply"
                  className="px-4 py-1.5 text-xs font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Add {Object.values(pickerSelected).filter(Boolean).length || ''} coat{Object.values(pickerSelected).filter(Boolean).length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}




// ── Header dropdowns row (inline-editable fields) ─────────────────────

/**
 * Renders one horizontal row of inline-editable dropdowns inside the
 * purple Concrete Restoration Quote Builder banner. Each <select>
 * PATCHes /appraisals/{id}/inline-field on change, then bubbles up
 * via `onChanged` so the parent re-pulls config (which refreshes
 * dependent rates like tile-defect rate when brand/profile changes).
 */
function HeaderDropdownsRow({ api, appraisalId, sources, values, ridgeCaps, onChanged }) {
  const [busy, setBusy] = useState({});
  const patch = async (field, value) => {
    setBusy((b) => ({ ...b, [field]: true }));
    try {
      await api.patch(`/appraisals/${appraisalId}/inline-field`, { field, value });
      if (onChanged) onChanged();
    } catch (_) { /* surface via toast if needed */ }
    finally {
      setBusy((b) => ({ ...b, [field]: false }));
    }
  };
  const cell = (label, field, options, valueKey, labelKey) => {
    const cur = values[field] || '';
    const isLoading = !!busy[field];
    return (
      <div className="flex flex-col gap-0.5 min-w-[10rem]">
        <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700/80 flex items-center gap-1">
          {label}{isLoading && <Loader2 size={9} className="animate-spin text-indigo-600" />}
        </span>
        <select
          data-testid={`header-${field}`}
          value={cur}
          onChange={(e) => patch(field, e.target.value)}
          disabled={isLoading}
          className="text-xs px-2 py-1 border border-indigo-300 rounded bg-white text-indigo-900 hover:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-300"
        >
          <option value="">— pick —</option>
          {(options || []).map((o) => {
            const v = typeof o === 'string' ? o : o[valueKey];
            const l = typeof o === 'string' ? o : (o[labelKey] || o[valueKey] || '');
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
      </div>
    );
  };
  return (
    <div className="mt-3 pt-3 border-t border-sky-200/80 flex flex-wrap gap-3" data-testid="header-dropdowns-row">
      {cell('Service Option', 'service_option', sources.service_options, 'key', 'label')}
      {cell('Roof Subtype', 'roof_subtype', sources.roof_subtypes, 'key', 'label')}
      {cell('Primary Material', 'primary_material', sources.primary_materials)}
      {cell('Tile Brand (override)', 'tile_brand_override', sources.tile_brands)}
      {cell('Tile Profile (override)', 'tile_profile_override', sources.tile_profiles)}
      {cell('Paint System (Stage 2)', 'restoration_paint_system_id',
        (sources.paint_systems || []).map(p => ({ key: p.id, label: `${p.brand || ''} · ${p.name}`.trim().replace(/^· /, '') })),
        'key', 'label')}
      {cell('Default Ridge Cap', 'default_ridge_cap_id',
        ridgeCaps.map(c => ({ key: c.id, label: `${c.name} · ${c.len_mm}mm` })),
        'key', 'label')}
    </div>
  );
}


// ── Jump-to-pin button (single-click → modal, double-click → tab) ─────

/**
 * Renders a pin reference (R1 / V1 / P1) as a click-target.
 * Single click → opens the master-plan preview modal with the pin
 * highlighted. Double click → asks the parent to switch to the Roof
 * Plan tab and scroll the diagram to the pin.
 */
function JumpToPinButton({ pinId, pinType, lengthM, diameterMm, onJumpToPin: _ignored }) {
  // Iter215 — per-pin "jump to diagram" link was confusing in practice
  // (tab-switch killed the builder's place) and overkill on every row.
  // Collapsed to a passive badge that shows the pin id + a hover tooltip
  // with type / length / diameter. To edit pins, use the "Edit on
  // diagram" button in each Component Section header (full-screen
  // annotator pop-out, returns the operator to the same builder spot).
  return (
    <span
      data-testid={`jump-to-pin-${pinId}`}
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-50 border border-orange-200 text-[11px] font-mono font-bold text-orange-700"
      title={`${pinType}${lengthM ? ` · ${lengthM.toFixed(1)}m` : ''}${diameterMm ? ` · Ø${diameterMm}mm` : ''}`}
    >
      <MapPin size={9} /> {pinId}
    </span>
  );
}

// Iter217 — reusable Confirm/Unconfirm chip used next to each pin
// badge across the Concrete builder sections (per-pin breakdown,
// valley replacements, aquaseal items). Click flips state.
function ConfirmChip({ rawId, confirmed, onConfirmPins, testidPrefix, testidIdx }) {
  if (!onConfirmPins || !rawId) return null;
  if (!confirmed) {
    return (
      <button
        type="button"
        data-testid={`${testidPrefix}-confirm-${testidIdx}`}
        onClick={() => onConfirmPins([rawId], true)}
        className="text-[9px] px-1.5 py-0 bg-amber-400 hover:bg-emerald-600 hover:text-white text-amber-900 rounded-full font-bold uppercase tracking-wider ring-1 ring-amber-500 transition-colors"
        title="Pending — click to confirm"
      >⚠ Pending</button>
    );
  }
  return (
    <button
      type="button"
      data-testid={`${testidPrefix}-unconfirm-${testidIdx}`}
      onClick={() => onConfirmPins([rawId], false)}
      className="text-[9px] px-1.5 py-0 bg-emerald-100 hover:bg-amber-100 text-emerald-700 hover:text-amber-800 rounded-full font-bold uppercase tracking-wider ring-1 ring-emerald-300 transition-colors opacity-60 hover:opacity-100"
      title="Confirmed — click to unconfirm"
    >✓</button>
  );
}
